import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useSearchParams, useOutletContext } from 'react-router-dom';
import { Plus, ArrowRight, Trophy, Trash2, X, Users, Upload, ChevronDown, ChevronUp, Search, FileText, Download, Filter, ChevronRight, ExternalLink, Loader2, History, Wand2, Clock } from 'lucide-react';
import Pagination from '../components/Pagination';
import PageHeader from '../components/PageHeader';
import { apiGet, apiPost, apiPut } from '../lib/api';
import { inrCompact, inr2, formatDate, statusColor, inr, fmtDMY } from '../lib/format';
import { toast } from 'sonner';
import { useFormFieldHelpers } from '../hooks/useFormFieldHelpers';
import { MaterialPicker } from '../components/pickers/MaterialPicker';
import VendorPicker from '../components/pickers/VendorPicker';
import { ConfirmDialog } from '../components/ConfirmDialog';
import POImportWizard from '../components/procurement/POImportWizard';
import ProcurementWizard from '../components/procurement/ProcurementWizard';
import { useAuth } from '../contexts/AuthContext';
import { VendorBandBadge } from '../components/VendorBandBadge';
import { RateIntelligence } from './RateIntelligence';
import RFQNegotiationDrawer from './procurement/RFQNegotiationDrawer';
import { useUrlTab } from '../lib/hooks/useUrlState';
import { downloadPdf as fetchPdf, pdfSafeName } from '../lib/downloadPdf';
import { ChargesTable, GstSplitHint, cleanCharges, chargesFromPo } from '../components/procurement/ChargesTable';
import { MprLinePicker } from '../components/procurement/MprLinePicker';
import { POEditModal, POHistory } from '../components/procurement/POEditModal';
import { ClubMprsPanel, ClubRfqModal } from '../components/procurement/ClubMprsPanel';
import { MprEditModal } from '../components/procurement/MprEditModal';
import { ItemwiseAwardModal } from '../components/procurement/ItemwiseAwardModal';

const PLAN_FREQUENCIES = ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Half-Yearly', 'Yearly', 'Entire Project'];
const SOURCE_BASES = ['WBS', 'BOQ', 'DPR'];

const canApproveMPR = (user) => {
  if (!user) return false;
  if (user.role === 'super_admin' || user.is_procurement_approver) return true;
  const dept = (user.department || '').toLowerCase();
  const role = (user.role || '').toLowerCase();
  if (['md', 'ceo', 'cmd', 'cfo'].includes(dept) || ['md', 'ceo', 'cmd', 'cfo'].includes(role)) return true;
  return dept === 'pm' || role === 'pm' || dept === 'project manager';
};

// A5 — approximate client-side mirror of approve_po()'s role x tier check
// (server remains the real guard; this only avoids showing an Approve
// button that would just 403). Tier + allowed_roles come from
// GET /approval-matrix; role comparison is exact-case, same as the backend.
const canApprovePO = (user, po, matrix) => {
  if (!user) return false;
  const role = (user.role || '').toLowerCase();
  // Followup round 2, P5: the server exempts exec roles from every tier's
  // role check (mirrors its self-approve bypass) — show Approve to them too.
  // R2 (Fixes round 4): Senior Leadership (role "user" + a senior profile/
  // designation, flagged server-side via is_senior_leadership) gets the same
  // override — matches the backend's _is_senior_leadership check.
  // #12: the server answers whether the user is in the Procurement approver
  // group (may approve at any value, including their own POs).
  if (user.is_procurement_approver) return true;
  const isExecOrSL = ['super_admin', 'md', 'ceo', 'cmd', 'cfo'].includes(role) || !!user.is_senior_leadership;
  if (isExecOrSL) return true;
  // R2: for everyone else, hide the button when the server would refuse —
  // a PO they created, or a value above their own approve limit.
  const uid = user.user_id || user.id;
  if (po?.created_by_id && uid && po.created_by_id === uid) return false;
  const value = Number(po?.total_value ?? po?.grand_total ?? po?.value ?? 0);
  if (user.po_approve_limit != null && value > Number(user.po_approve_limit)) return false;
  const tier = (matrix || []).find(t => t.max == null || value <= t.max);
  // A "Project's PM" tier is approvable only by that specific project's PM —
  // we can't verify PM identity client-side without an extra fetch per row,
  // so (execs aside, above) don't show a button the server would 403.
  if (tier?.rule === 'project_pm') return false;
  const allowed = tier?.allowed_roles?.length ? tier.allowed_roles : ['MD'];
  // S5 fix (30-Sep-2026 Stores doc): a named tier member (allowed_member_ids)
  // must also see Approve, not only users whose role is in allowed_roles.
  return allowed.includes(user.role) || (uid && (tier?.allowed_member_ids || []).includes(uid));
};

export const mprPickerLabel = (m) => {
  const n = (m.line_items || m.items || []).length || 1;
  const req = m.required_by || m.required_date;
  return `${m.mpr_no} · ${fmtDMY(m.created_at)} · ${n} item${n === 1 ? '' : 's'}${req ? ` · required by ${fmtDMY(req)}` : ''}`;
};

const MprSiteLine = ({ projectId }) => {
  const [p, setP] = useState(null);
  useEffect(() => {
    if (!projectId) return;
    apiGet(`/projects/${projectId}`).then(setP).catch(() => setP(null));
  }, [projectId]);
  if (!p) return null;
  const city = [p.site_city, p.site_state].filter(Boolean).join(', ') + (p.site_pincode ? ` - ${p.site_pincode}` : '');
  return (
    <p className="text-xs text-slate-500 mt-0.5 whitespace-pre-line" data-testid="mpr-detail-site">
      {p.code ? `${p.code} · ` : ''}Site: {[p.site_address, city].filter(Boolean).join(', ') || '—'}
    </p>
  );
};

const POTotalsPreview = ({ items, charges }) => {
  const sub = items.reduce((t, it) => t + Number(it.qty || 0) * Number(it.rate || 0), 0);
  const lineGst = items.reduce((t, it) => t + Number(it.qty || 0) * Number(it.rate || 0) * Number(it.gst_rate || 0) / 100, 0);
  const ch = (charges || []).reduce((t, c) => t + Number(c.amount || 0), 0);
  const chGst = (charges || []).reduce((t, c) => t + Number(c.amount || 0) * Number(c.gst_rate || 0) / 100, 0);
  return (
    <div className="ml-auto w-72 text-xs border border-slate-200 rounded-sm p-2 space-y-0.5" data-testid="po-totals-preview">
      <div className="flex justify-between"><span>Materials</span><span className="tabular-nums">{inr2(sub)}</span></div>
      <div className="flex justify-between"><span>Charges</span><span className="tabular-nums">{inr2(ch)}</span></div>
      <div className="flex justify-between"><span>GST (lines + charges)</span><span className="tabular-nums">{inr2(lineGst + chGst)}</span></div>
      <div className="flex justify-between font-bold text-[#3b4872] text-sm"><span>Grand total (est.)</span><span className="tabular-nums" data-testid="po-grand-preview">{inr2(Math.round(sub + ch + lineGst + chGst))}</span></div>
    </div>
  );
};

const mprStatusTag = (s) => {
  const map = {
    'Draft': 'Pending',
    'Pending PM Approval': 'Pending',
    'PM Approved': 'Approved',
    'RFQ Issued': 'In Progress',
    'Partially Ordered': 'Partially Received',
    'Awarded': 'In Progress',
    'Closed': 'In Progress',
  };
  return statusColor(map[s] || 'Pending');
};

const TABS = ['MPR', 'RFQ', 'Comparative Statement', 'Purchase Orders', 'Rate Intelligence', 'Approval Matrix'];

export default function Procurement() {
  const { activeProject } = useOutletContext() || {};
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'MPR';
  const activeRfq = searchParams.get('rfq') || null;

  const setTab = (t) => setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('tab', t); if (t !== 'Comparative Statement') p.delete('rfq'); return p; });
  const setActiveRfq = (id) => setSearchParams(prev => { const p = new URLSearchParams(prev); if (id) p.set('rfq', id); else p.delete('rfq'); return p; });

  const [kCards, setKCards] = useState({ mprPending: 0, rfqOpen: 0, poPending: 0, empanelPending: 0 });
  const [refreshKey, setRefreshKey] = useState(0);
  const [matrix, setMatrix] = useState([]);
  const [showWizard, setShowWizard] = useState(false);
  const [wizardType, setWizardType] = useState(null);

  const openWizard = (type) => { setWizardType(type); setShowWizard(true); };

  const load = async () => {
    try {
      const [mprR, rfqR, poR, m, emp] = await Promise.all([
        apiGet('/mpr', { status: 'Pending PM Approval', limit: 1 }).catch(() => ({ total: 0 })),
        apiGet('/rfq', { status: 'Open', limit: 1 }).catch(() => ({ total: 0 })),
        apiGet('/po', { limit: 1 }).catch(() => ({ facets: {} })),
        apiGet('/approval-matrix').catch(() => ({ tiers: [] })),
        apiGet('/vendors/empanelment-pending-count').catch(() => ({ total: 0 })),
      ]);
      setKCards({
        mprPending: mprR?.total || 0,
        rfqOpen: rfqR?.total || 0,
        poPending: (poR?.facets?.status?.Pending) || 0,
        empanelPending: emp?.total || 0,
      });
      setMatrix(m?.tiers || []);
      setRefreshKey(k => k + 1);
    } catch {}
  };
  useEffect(() => { load(); }, []);
  const [cpCheck, setCpCheck] = useState(null);
  useEffect(() => { apiGet('/settings/company-profile/completeness').then(setCpCheck).catch(() => {}); }, []);

  return (
    <div>
      {cpCheck && !cpCheck.complete && cpCheck.can_edit && (
        <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-300 text-amber-800 text-sm rounded-sm" data-testid="company-profile-incomplete-banner">
          Company Profile is incomplete — POs are printing without HO address / GSTIN / PAN. Complete it in{' '}
          <a href="/app/settings/company-profile" className="font-semibold underline">Settings → Company Profile</a>.
          <span className="text-xs text-amber-700 ml-1">(Missing: {cpCheck.missing.join(', ')})</span>
        </div>
      )}
      <PageHeader
        overline="Procurement"
        title="MPR → RFQ → CS → PO Workflow"
        subtitle="Approval-matrix-driven procurement with auto-generated comparative statements."
        actions={
          <button onClick={() => openWizard('po')} data-testid="proc-new-wizard-btn"
            className="flex items-center gap-2 px-4 py-2 bg-[#3b4872] text-white text-sm font-display font-semibold rounded-sm hover:bg-[#2c3658] transition-colors">
            <Wand2 size={14} /> New PO
          </button>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KCard label="MPRs Pending PM" value={kCards.mprPending} />
        <KCard label="Open RFQs" value={kCards.rfqOpen} />
        <KCard label="Pending POs" value={kCards.poPending} />
        <KCard
          label="Vendors Awaiting Empanelment"
          value={kCards.empanelPending}
          testid="proc-kpi-empanel-pending"
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="border-b border-slate-200 flex overflow-x-auto">
          {TABS.map(t => (
            <button key={t} onClick={()=>setTab(t)} data-testid={`proc-tab-${t.toLowerCase().replace(/\s+/g,'-')}`}
              className={`relative px-5 py-3 text-sm font-display font-semibold whitespace-nowrap transition-colors ${tab === t ? 'text-[#0F172A]' : 'text-slate-500 hover:text-[#0F172A]'}`}>
              {t}
              {tab === t && <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-[#3b4872]" />}
            </button>
          ))}
        </div>
        <div className="p-5">
          {tab === 'MPR' && <MPRPanel onReload={load} refreshKey={refreshKey} setTab={setTab} setActiveRfq={setActiveRfq} activeProject={activeProject} />}
          {tab === 'RFQ' && <RFQPanel onReload={load} refreshKey={refreshKey} onView={(id)=>{ setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('tab','Comparative Statement'); p.set('rfq', id); return p; }); }} />}
          {tab === 'Comparative Statement' && <CSPanel rfqId={activeRfq} setRfqId={setActiveRfq} onReload={load} refreshKey={refreshKey} setTab={setTab} />}
          {tab === 'Purchase Orders' && <POPanel onReload={load} refreshKey={refreshKey} onOpenWizard={() => openWizard('po')} activeProject={activeProject} matrix={matrix} />}
          {tab === 'Rate Intelligence' && <RateIntelligence />}
          {tab === 'Approval Matrix' && <ApprovalMatrix matrix={matrix} />}
        </div>
      </div>

      {showWizard && (
        <ProcurementWizard
          defaultType={wizardType}
          defaultProjectId={activeProject}
          onClose={() => { setShowWizard(false); setWizardType(null); }}
          onSaved={() => { load(); setRefreshKey(k => k + 1); }}
        />
      )}
    </div>
  );
}

const KCard = ({ label, value, testid }) => (
  <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid={testid}>
    <div className="kpi-overline">{label}</div>
    <div className="font-display text-2xl font-extrabold tracking-tight mt-1">{value}</div>
  </div>
);

// ----- MPR -----
const MPRPanel = ({ onReload, refreshKey, setTab, setActiveRfq, activeProject }) => {
  const { user } = useAuth();
  const isApprover = canApproveMPR(user);
  const [convertingId, setConvertingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [selectedVendorIds, setSelectedVendorIds] = useState([]);
  const [vendorSearch, setVendorSearch] = useState('');
  const [rcMatches, setRcMatches] = useState([]);

  // Server-side data state
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [tabCounts, setTabCounts] = useState({ active: 0, completed: 0 });

  // Filters + sort + toggle
  const [activeTab, setActiveTab] = useUrlTab('sub', 'active');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSelf, setFilterSelf] = useState(false);
  const [clubGroups, setClubGroups] = useState([]);
  const [clubOpenKey, setClubOpenKey] = useState(null);
  const [clubPO, setClubPO] = useState(null);
  const clubPreset = useMemo(() => (clubPO ? { lines: clubPO.lines } : null), [clubPO]);
  const [clubRFQ, setClubRFQ] = useState(null);
  const [editMpr, setEditMpr] = useState(null);
  const [sortKey, setSortKey] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedMPR, setSelectedMPR] = useState(null);
  const searchTimerRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce text search
  useEffect(() => {
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => { setDebouncedSearch(filterSearch); setPage(1); }, 300);
    return () => clearTimeout(searchTimerRef.current);
  }, [filterSearch]);

  // Reset to page 1 on filter/tab changes
  useEffect(() => { setPage(1); }, [filterStatus, activeTab]);

  // Fetch tab counts
  useEffect(() => {
    Promise.all([
      apiGet('/mpr', { completed: false, limit: 1 }).catch(() => ({ total: 0 })),
      apiGet('/mpr', { completed: true, limit: 1 }).catch(() => ({ total: 0 })),
    ]).then(([a, c]) => setTabCounts({ active: a?.total || 0, completed: c?.total || 0 }));
  }, [refreshKey]);

  // Main data fetch
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { page, limit };
    if (debouncedSearch) params.q = debouncedSearch;
    if (filterStatus) params.status = filterStatus;
    if (filterSelf) params.self_approved = true;
    else params.completed = activeTab === 'completed';
    // A4 — scope to the top-nav's selected project, same as the rest of the app.
    if (activeProject) params.project_id = activeProject;
    apiGet('/mpr', params).then(r => {
      if (!cancelled) { setItems(r?.items || []); setTotal(r?.total || 0); }
    }).catch(() => {}).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, limit, debouncedSearch, filterStatus, activeTab, refreshKey, activeProject, filterSelf]);

  useEffect(() => {
    // Initial load of vendor list (approved vendors only — for the convert-to-RFQ picker).
    apiGet('/vendors', { limit: 2000, empanelment_status: 'approved' }).then(d => setVendors(d?.items || [])).catch(()=>{});
  }, []);

  // Re-fetch vendors each time the RFQ convert modal opens — picks up any new vendors.
  // Round 4 P1: only approved vendors are invitable.
  useEffect(() => {
    if (!convertingId) return;
    apiGet('/vendors', { limit: 2000, empanelment_status: 'approved' }).then(d => {
      setVendors((d?.items || []).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    }).catch(() => {});
  }, [convertingId]);

  useEffect(() => {
    if (!convertingId) { setRcMatches([]); return; }
    const matched = vendors.filter(v => selectedVendorIds.includes(v.id)).slice(0, 5);
    if (!matched.length) { setRcMatches([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      const found = [];
      for (const v of matched) {
        try {
          const chk = await apiGet('/rfq/rate-contract-check', { vendor_id: v.id });
          if (chk?.can_skip_rfq) found.push({ vendor: v.name, rc: chk.rate_contract });
        } catch {}
      }
      if (!cancelled) setRcMatches(found);
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [convertingId, selectedVendorIds, vendors]);

  const submitForApproval = async (m) => {
    setBusyId(m.id);
    try {
      const sr = await apiPost(`/mpr/${m.id}/submit`, {});
      toast.success('Sent to Project Manager for approval');
      if (sr?.no_pm_warning) toast.warning(sr.no_pm_warning);
      onReload();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to submit'); }
    finally { setBusyId(null); }
  };

  const approve = async (m) => {
    setBusyId(m.id);
    try {
      await apiPost(`/mpr/${m.id}/pm-approve`, {});
      toast.success('MPR approved');
      onReload();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to approve'); }
    finally { setBusyId(null); }
  };

  const reject = async () => {
    if (!rejectReason.trim()) { toast.error('A rejection reason is required'); return; }
    setBusyId(rejectingId);
    try {
      await apiPost(`/mpr/${rejectingId}/pm-reject`, { reason: rejectReason.trim() });
      toast.success('MPR rejected — returned to Draft');
      setRejectingId(null); setRejectReason('');
      onReload();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to reject'); }
    finally { setBusyId(null); }
  };

  const convert = async (mpr, vendorIds) => {
    if (!vendorIds || vendorIds.length === 0) {
      toast.error('Select at least one vendor to invite');
      return;
    }
    setBusyId(mpr.id);
    try {
      const lineItems = mprItems(mpr);
      let rfq;
      if (lineItems.length > 1) {
        // Multi-item MPR: use the multi-item-aware endpoint
        const res = await apiPost(`/mpr/${mpr.id}/create-rfq-for-items`, {
          vendor_ids: vendorIds,
          items: lineItems.map(li => ({ item_id: li.id || li.material_id })),
          split: 'single',
        });
        rfq = (res?.rfqs || [])[0] || res;
      } else {
        // Single-item MPR: legacy endpoint (unchanged)
        const selectedNames = vendorIds.map(id => vendors.find(v => v.id === id)?.name).filter(Boolean);
        rfq = await apiPost(`/mpr/${mpr.id}/convert-to-rfq`, { vendors: selectedNames, vendor_ids: vendorIds });
      }
      toast.success(`RFQ ${rfq.rfq_no} created`);
      setConvertingId(null);
      setSelectedVendorIds([]);
      setVendorSearch('');
      onReload();
      setActiveRfq(rfq.id);
      setTab('RFQ');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to convert'); }
    finally { setBusyId(null); }
  };

  // ── Helpers: normalize both old and new MPR schemas ──
  const mprProject = (m) => m.project_name || m.project || '—';
  const mprItems = (m) => {
    if (m.line_items?.length) return m.line_items;
    if (m.item) return [{ material_name: m.item, qty: m.quantity, qty_required: m.quantity, unit: m.uom || m.unit }];
    return [];
  };
  const mprItemSummary = (m) => {
    const its = mprItems(m);
    if (!its.length) return '—';
    if (its.length === 1) return `${its[0].material_name || '—'} · ${its[0].qty ?? its[0].qty_required ?? m.quantity ?? ''} ${its[0].unit || its[0].uom || m.uom || ''}`.trim();
    return `${its.length} items`;
  };

  // Client-side project filter + sort on fetched page (server handles search/status/pagination)
  const projectOptions = [...new Set(items.map(mprProject).filter(Boolean))].sort();
  const statusOptions  = [...new Set(items.map(m => m.status || '').filter(Boolean))].sort();
  const displayItems = filterProject ? items.filter(m => mprProject(m) === filterProject) : items;
  const sorted = [...displayItems].sort((a, b) => {
    const val = (m, key) => {
      if (key === 'project_name') return mprProject(m);
      if (key === 'item_summary') return mprItemSummary(m);
      return m[key] || '';
    };
    const va = val(a, sortKey), vb = val(b, sortKey);
    const cmp = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };
  const SortIcon = ({ k }) => sortKey !== k
    ? <span className="text-slate-400 ml-0.5 text-[10px]">↕</span>
    : <span className="text-[#3b4872] ml-0.5 text-[10px] font-bold">{sortDir === 'asc' ? '↑' : '↓'}</span>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="kpi-overline">Material Requisition Plan</div>
          <h3 className="font-display font-bold text-base mt-1">{total} MPRs · {tabCounts.active > 0 ? `${tabCounts.active} active` : ''}</h3>
        </div>
        <span className="text-[10px] text-slate-400 font-display uppercase tracking-wider" data-testid="mpr-create-moved-hint">
          MPRs are raised in Store &amp; Inventory → MPR
        </span>
      </div>
      <ClubMprsPanel openKey={clubOpenKey} onGroups={setClubGroups}
        onCreatePO={g => setClubPO(g)} onCreateRFQ={g => setClubRFQ(g)} />
      {clubPO && <ManualPOModal preset={clubPreset} onClose={() => setClubPO(null)} onSaved={() => { setClubPO(null); onReload?.(); }} />}
      {clubRFQ && <ClubRfqModal group={clubRFQ} onClose={() => setClubRFQ(null)} onDone={() => { setClubRFQ(null); onReload?.(); }} />}
      {editMpr && <MprEditModal mpr={editMpr} onClose={() => setEditMpr(null)} onSaved={() => { setEditMpr(null); setSelectedMPR(null); onReload?.(); }} />}

      {/* ── Active / Completed toggle ── */}
      <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 w-fit mb-3">
        {[['active','Active'],['completed','Completed']].map(([val,label]) => (
          <button key={val} onClick={() => { setActiveTab(val); setFilterStatus(''); setFilterProject(''); setFilterSearch(''); setPage(1); }}
            className={`px-4 py-1.5 text-[10px] font-display font-bold uppercase tracking-wider rounded-md transition-colors ${activeTab===val ? 'bg-white text-[#3b4872] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid={`mpr-tab-${val}`}>
            {label} ({tabCounts[val] ?? 0})
          </button>
        ))}
      </div>

      {/* ── Filters ── */}
      <div className="flex flex-wrap gap-2 items-center mb-4">
        <input type="search" placeholder="Search MPR no, project, material…"
          value={filterSearch} onChange={e => setFilterSearch(e.target.value)}
          className="border border-slate-300 rounded-sm px-3 py-1.5 text-xs focus:outline-none focus:border-[#3b4872] w-60"
          data-testid="mpr-filter-search" />
        <select value={filterProject} onChange={e => setFilterProject(e.target.value)}
          className="border border-slate-300 rounded-sm px-3 py-1.5 text-xs focus:outline-none focus:border-[#3b4872] bg-white"
          data-testid="mpr-filter-project">
          <option value="">All Projects (this page)</option>
          {projectOptions.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="border border-slate-300 rounded-sm px-3 py-1.5 text-xs focus:outline-none focus:border-[#3b4872] bg-white"
          data-testid="mpr-filter-status">
          <option value="">All Statuses</option>
          {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={() => setFilterSelf(v => !v)} data-testid="mpr-self-approved-filter"
          className={`text-[10px] px-2 py-1 rounded-full border font-display font-semibold ${filterSelf ? 'bg-amber-500 text-white border-amber-500' : 'border-amber-300 text-amber-700'}`}>
          Self-approved
        </button>
        {(filterSearch || filterProject || filterStatus) && (
          <button onClick={() => { setFilterSearch(''); setFilterProject(''); setFilterStatus(''); setPage(1); }}
            className="text-[10px] text-slate-500 hover:text-rose-600 underline">Clear</button>
        )}
        {loading && <span className="text-[10px] text-slate-400">Loading…</span>}
      </div>

      <table className="w-full dense-table">
        <thead>
          <tr>
            <th className="cursor-pointer select-none hover:text-[#3b4872]" onClick={() => toggleSort('mpr_no')}>MPR No <SortIcon k="mpr_no" /></th>
            <th className="cursor-pointer select-none hover:text-[#3b4872]" onClick={() => toggleSort('created_at')}>Date Created <SortIcon k="created_at" /></th>
            <th className="cursor-pointer select-none hover:text-[#3b4872]" onClick={() => toggleSort('project_name')}>Project <SortIcon k="project_name" /></th>
            <th>Item(s)</th>
            <th>Plan Freq.</th>
            <th>Basis</th>
            <th>Required By</th>
            <th className="cursor-pointer select-none hover:text-[#3b4872]" onClick={() => toggleSort('status')}>Status <SortIcon k="status" /></th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && !loading && <tr><td colSpan="9" className="text-center py-12 text-slate-400 text-xs uppercase tracking-widest font-display">
            {(filterSearch||filterProject||filterStatus) ? 'No results match the filters' : activeTab==='completed' ? 'No completed MPRs' : 'No active MPRs'}
          </td></tr>}
          {sorted.map(m => {
            const its = mprItems(m);
            const createdAt = m.created_at ? formatDate(m.created_at) : '—';
            return (
              <tr key={m.id} data-testid={`mpr-row-${m.id}`}
                className="cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => setSelectedMPR(m)}>
                <td className="font-mono text-xs">{m.mpr_no}
                  {(() => { const g = clubGroups.find(x => x.lines.some(l => l.mpr_id === m.id)); if (!g) return null;
                    const n = new Set(g.lines.map(l => l.mpr_id)).size - 1;
                    return <button onClick={e => { e.stopPropagation(); setClubOpenKey({ key: g.group_key, t: Date.now() }); }} className="ml-1 px-1.5 py-0.5 text-[9px] rounded-full bg-indigo-100 text-[#3b4872] font-sans font-semibold" data-testid={`mpr-club-chip-${m.id}`}>Club suggestion · {n} other MPR{n === 1 ? "" : "s"}</button>; })()}
                </td>
                <td className="text-xs whitespace-nowrap">{createdAt}</td>
                <td className="max-w-[180px] truncate" title={mprProject(m)}>{mprProject(m)}</td>
                <td className="text-xs">
                  {its.length > 1
                    ? <span className="font-display font-semibold text-[#3b4872]">{its.length} items</span>
                    : mprItemSummary(m)}
                </td>
                <td className="text-xs">{m.plan_frequency || '—'}</td>
                <td className="text-xs">{m.source_basis || '—'}</td>
                <td>{formatDate(m.required_by)}</td>
                <td>
                  <span className={`tag ${mprStatusTag(m.status)}`} data-testid={`mpr-status-${m.id}`}>{m.status}</span>
                  {m.status === 'Draft' && m.rejection_reason && (
                    <div className="text-[10px] text-rose-600 mt-0.5" title={m.rejection_reason}>Rejected: {m.rejection_reason}</div>
                  )}
                </td>
                <td onClick={e => e.stopPropagation()}>
                  <div className="flex items-center gap-2 justify-end">
                    {(m.status === 'Draft') && (
                      <button data-testid={`submit-mpr-btn-${m.id}`} disabled={busyId===m.id} onClick={()=>submitForApproval(m)} className="text-xs text-[#3b4872] font-display font-semibold hover:underline disabled:opacity-40">Submit for Approval</button>
                    )}
                    {(m.status === 'Pending PM Approval') && isApprover && (
                      <>
                        <button data-testid={`approve-mpr-btn-${m.id}`} disabled={busyId===m.id} onClick={()=>approve(m)} className="text-xs text-emerald-700 font-display font-semibold hover:underline disabled:opacity-40">Approve</button>
                        <button data-testid={`reject-mpr-btn-${m.id}`} disabled={busyId===m.id} onClick={()=>{setRejectingId(m.id); setRejectReason('');}} className="text-xs text-rose-600 font-display font-semibold hover:underline disabled:opacity-40">Reject</button>
                      </>
                    )}
                    {(m.status === 'Pending PM Approval') && !isApprover && (
                      <span className="text-[10px] text-amber-600 font-display uppercase tracking-wider">Awaiting PM</span>
                    )}
                    {(m.status === 'PM Approved') && (
                      <button data-testid={`convert-mpr-btn-${m.id}`} onClick={()=>setConvertingId(m.id)} className="text-xs text-[#3b4872] font-display font-semibold flex items-center gap-1 hover:underline">Convert to RFQ <ArrowRight size={12} /></button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {total > limit && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <Pagination
            page={page} pageSize={limit} total={total}
            start={(page - 1) * limit} end={Math.min(page * limit, total)}
            onPageChange={setPage} onPageSizeChange={setLimit}
            pageSizeOptions={[20, 50, 100]} label="MPRs"
            data-testid="mpr-pagination"
          />
        </div>
      )}

      {/* ── MPR Detail Modal ── */}
      {selectedMPR && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedMPR(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-sm shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-start justify-between">
              <div>
                <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872]">MPR Detail</div>
                <h3 className="font-display font-bold text-lg mt-0.5 font-mono">{selectedMPR.mpr_no}</h3>
              </div>
              <button onClick={() => setSelectedMPR(null)} className="text-slate-400 hover:text-slate-700 mt-1"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div className="col-span-2"><span className="kpi-overline">Project</span><p className="mt-0.5">{mprProject(selectedMPR)}</p><MprSiteLine projectId={selectedMPR.project_id} /></div>
                {selectedMPR.self_approved && selectedMPR.self_approved_basis === 'procurement_group' && <div className="col-span-2"><span className="tag bg-amber-100 text-amber-700" data-testid="mpr-self-approved-badge">Self-approved (Procurement team)</span></div>}
                <div><span className="kpi-overline">Status</span><p className="mt-0.5"><span className={`tag ${mprStatusTag(selectedMPR.status)}`}>{selectedMPR.status}</span></p></div>
                <div><span className="kpi-overline">Date Created</span><p className="mt-0.5">{selectedMPR.created_at ? formatDate(selectedMPR.created_at) : '—'}</p></div>
                <div><span className="kpi-overline">Required By</span><p className="mt-0.5">{formatDate(selectedMPR.required_by) || '—'}</p></div>
                <div><span className="kpi-overline">Plan Frequency</span><p className="mt-0.5">{selectedMPR.plan_frequency || '—'}</p></div>
                <div><span className="kpi-overline">Source Basis</span><p className="mt-0.5">{selectedMPR.source_basis || '—'}</p></div>
                {selectedMPR.requested_by && <div><span className="kpi-overline">Requested By</span><p className="mt-0.5">{selectedMPR.requested_by}</p></div>}
                {selectedMPR.approved_by && <div><span className="kpi-overline">Approved By</span><p className="mt-0.5">{selectedMPR.approved_by}</p></div>}
              </div>
              {selectedMPR.justification && (
                <div><span className="kpi-overline">Justification</span><p className="mt-0.5 text-sm text-slate-700">{selectedMPR.justification}</p></div>
              )}
              <div>
                <div className="kpi-overline mb-2">Line Items ({mprItems(selectedMPR).length})</div>
                <table className="w-full dense-table">
                  <thead><tr><th>#</th><th>Material</th><th>Category</th><th className="text-right">Qty</th><th>Unit</th><th>Remarks</th></tr></thead>
                  <tbody>
                    {mprItems(selectedMPR).map((item, idx) => (
                      <tr key={idx}>
                        <td className="text-xs text-slate-500">{idx + 1}</td>
                        <td className="font-medium text-sm">{item.material_name || item.item || '—'}</td>
                        <td className="text-xs">{item.category || item.material_category || '—'}</td>
                        <td className="text-right tabular-nums font-display font-bold">{item.qty ?? item.qty_required ?? item.quantity ?? '—'}</td>
                        <td className="text-xs">{item.unit || item.uom || '—'}</td>
                        <td className="text-xs text-slate-500">{item.remarks || item.notes || '—'}</td>
                      </tr>
                    ))}
                    {mprItems(selectedMPR).length === 0 && (
                      <tr><td colSpan="6" className="text-center text-slate-400 text-xs py-4">No items</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="sticky bottom-0 bg-slate-50 border-t border-slate-200 px-5 py-3 flex justify-end gap-2">
              {['Draft', 'Pending PM Approval', 'PM Rejected', 'PM Approved'].includes(selectedMPR.status) && (
                <button onClick={() => setEditMpr(selectedMPR)} className="px-4 py-2 text-xs border border-[#3b4872] text-[#3b4872] rounded-sm font-display font-semibold" data-testid="mpr-drawer-edit-btn">Edit</button>
              )}
              {selectedMPR.status === 'Draft' && (
                <button onClick={() => { submitForApproval(selectedMPR); setSelectedMPR(null); }}
                  className="px-4 py-2 text-xs bg-[#3b4872] text-white rounded-sm font-display font-semibold">Submit for Approval</button>
              )}
              {selectedMPR.status === 'Pending PM Approval' && isApprover && (
                <>
                  <button onClick={() => { approve(selectedMPR); setSelectedMPR(null); }}
                    className="px-4 py-2 text-xs bg-emerald-600 text-white rounded-sm font-display font-semibold">Approve</button>
                  <button onClick={() => { setRejectingId(selectedMPR.id); setRejectReason(''); setSelectedMPR(null); }}
                    className="px-4 py-2 text-xs bg-rose-600 text-white rounded-sm font-display font-semibold">Reject</button>
                </>
              )}
              {selectedMPR.status === 'PM Approved' && (
                <button onClick={() => { setConvertingId(selectedMPR.id); setSelectedMPR(null); }}
                  className="px-4 py-2 text-xs bg-[#3b4872] text-white rounded-sm font-display font-semibold flex items-center gap-1">Convert to RFQ <ArrowRight size={12} /></button>
              )}
              <button onClick={() => setSelectedMPR(null)} className="px-4 py-2 text-xs border border-slate-300 rounded-sm font-display hover:bg-slate-50">Close</button>
            </div>
          </div>
        </div>
      )}

      {convertingId && (() => {
        const filteredVendors = vendors.filter(v =>
          !vendorSearch ||
          (v.name || '').toLowerCase().includes(vendorSearch.toLowerCase()) ||
          (v.code || v.vendor_code || '').toLowerCase().includes(vendorSearch.toLowerCase())
        );
        const toggleVendor = (vid) => setSelectedVendorIds(prev =>
          prev.includes(vid) ? prev.filter(id => id !== vid) : [...prev, vid]
        );
        return (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div onClick={e=>e.stopPropagation()} className="bg-white rounded-sm p-5 w-full max-w-lg">
              <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872]">Convert</div>
              <h3 className="font-display font-bold text-xl mt-1 mb-4">MPR → RFQ</h3>
              {rcMatches.length > 0 && (
                <div className="mb-3 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-sm" data-testid="rate-contract-banner">
                  {rcMatches.map((m, i) => (
                    <div key={i} className="text-xs text-emerald-800">
                      ✓ <span className="font-display font-bold">{m.vendor}</span> has an active rate contract — you may proceed directly to PO.
                    </div>
                  ))}
                </div>
              )}
              <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">
                Select Vendors to Invite ({selectedVendorIds.length} selected)
              </label>
              <input
                type="text"
                placeholder="Search vendor name or code…"
                value={vendorSearch}
                onChange={e => setVendorSearch(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm mb-2 focus:border-[#3b4872] focus:outline-none"
                data-testid="rfq-vendor-search"
                autoFocus
              />
              <div className="border border-slate-200 rounded-sm max-h-52 overflow-y-auto divide-y divide-slate-100">
                {filteredVendors.length === 0 && (
                  <div className="px-3 py-3 text-xs text-slate-400 italic text-center">
                    {vendorSearch ? `No vendors match "${vendorSearch}"` : 'No vendors in master'}
                  </div>
                )}
                {filteredVendors.map(v => {
                  const isSelected = selectedVendorIds.includes(v.id);
                  return (
                    <div
                      key={v.id}
                      onClick={() => !v.incomplete && toggleVendor(v.id)}
                      className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${
                        v.incomplete ? 'opacity-40 cursor-not-allowed' :
                        isSelected ? 'bg-indigo-50' : 'hover:bg-slate-50'
                      }`}
                      data-testid={`rfq-vendor-option-${v.id}`}
                    >
                      <input type="checkbox" checked={isSelected} readOnly className="w-4 h-4 accent-[#3b4872] flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">
                          {v.name}
                          {v.incomplete && <span className="ml-1 text-[10px] text-amber-600">⚠ incomplete</span>}
                        </div>
                        {(v.code || v.vendor_code || v.category) && (
                          <div className="text-[10px] text-slate-400">
                            {v.code || v.vendor_code || ''}{v.category ? ` · ${v.category}` : ''}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {selectedVendorIds.length > 0 && (
                <div className="mt-2 text-xs text-slate-600">
                  Selected: {selectedVendorIds.map(id => vendors.find(v => v.id === id)?.name).filter(Boolean).join(', ')}
                </div>
              )}
              <div className="flex justify-between items-center mt-4 pt-3 border-t border-slate-200">
                <button
                  onClick={() => setSelectedVendorIds([])}
                  className="text-xs text-slate-500 hover:text-slate-700"
                >
                  Clear selection
                </button>
                <div className="flex gap-2">
                  <button onClick={() => { setConvertingId(null); setSelectedVendorIds([]); setVendorSearch(''); }}
                    className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50 font-display">Cancel</button>
                  <button
                    data-testid="confirm-convert-btn"
                    onClick={() => convert(items.find(x=>x.id===convertingId), selectedVendorIds)}
                    disabled={selectedVendorIds.length === 0 || busyId === convertingId}
                    className="px-4 py-2 text-sm bg-[#3b4872] text-white rounded-sm font-display font-semibold disabled:opacity-40"
                  >
                    Issue RFQ ({selectedVendorIds.length})
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {rejectingId && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div onClick={e=>e.stopPropagation()} className="bg-white rounded-sm p-5 w-full max-w-md">
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-rose-600">Reject MPR</div>
            <h3 className="font-display font-bold text-xl mt-1 mb-4">Return to Stores for re-work</h3>
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-2">Reason for rejection *</label>
            <textarea data-testid="mpr-reject-reason" value={rejectReason} onChange={e=>setRejectReason(e.target.value)} rows={3} placeholder="e.g. Quantity too high for this period — split into weekly plan" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-rose-500 focus:ring-1 focus:ring-rose-500 focus:outline-none" />
            <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-slate-200">
              <button onClick={()=>{setRejectingId(null); setRejectReason('');}} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50 font-display">Cancel</button>
              <button data-testid="confirm-reject-btn" disabled={busyId===rejectingId} onClick={reject} className="px-4 py-2 text-sm bg-rose-600 text-white rounded-sm font-display font-semibold disabled:opacity-40">Reject MPR</button>
            </div>
          </div>
        </div>
      )}

      {/* MPR creation moved to the Store & Inventory module */}
    </div>
  );
};

// ----- RFQ -----
const RFQPanel = ({ onReload, refreshKey, onView }) => {
  const [quoteOf, setQuoteOf] = useState(null); // full RFQ object (not just id)
  const [quote, setQuote] = useState({});
  const [lineRates, setLineRates] = useState({}); // { item_id: rate } for multi-item
  const [negotiating, setNegotiating] = useState(null);
  const [historyRfq, setHistoryRfq] = useState(null); // { id, rfq_no } for history modal

  // Server-side data state
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [filterSearch, setFilterSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimerRef = useRef(null);

  useEffect(() => {
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => { setDebouncedSearch(filterSearch); setPage(1); }, 300);
    return () => clearTimeout(searchTimerRef.current);
  }, [filterSearch]);

  useEffect(() => { setPage(1); }, [filterStatus]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { page, limit };
    if (debouncedSearch) params.q = debouncedSearch;
    if (filterStatus) params.status = filterStatus;
    apiGet('/rfq', params).then(r => {
      if (!cancelled) { setItems(r?.items || []); setTotal(r?.total || 0); }
    }).catch(() => {}).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, limit, debouncedSearch, filterStatus, refreshKey]);

  const submit = async (e) => {
    e.preventDefault();
    const isMultiItem = (quoteOf?.line_items?.length || 0) > 1;
    // Round 4 P1 — vendor must be picked from Vendor Master (empanelled only).
    if (!quote.vendor_id) { toast.error('Pick an empanelled vendor from the Vendor Master'); return; }
    if (!quote.delivery_days) { toast.error('Delivery days is required'); return; }
    if (!quote.payment_terms?.trim()) { toast.error('Payment terms is required'); return; }
    if (!quote.validity?.trim()) { toast.error('Validity is required'); return; }
    if (isMultiItem) {
      const hasRates = quoteOf.line_items.every((li, idx) => {
        const r = parseFloat(lineRates[li.id || li.material_id || idx] || 0);
        return r > 0;
      });
      if (!hasRates) { toast.error('Enter a rate for every line item'); return; }
    } else {
      if (!quote.rate || Number(quote.rate) <= 0) { toast.error('Rate is required'); return; }
    }

    const rfqId = quoteOf.id;
    try {
      if (isMultiItem) {
        const lineItemsPayload = quoteOf.line_items.map((li, idx) => ({
          id: li.id || li.material_id,
          material_name: li.material_name || li.item || '',
          qty: parseFloat(li.qty || li.quantity || 0),
          uom: li.uom || li.unit || '',
          rate: parseFloat(lineRates[li.id || li.material_id || idx] || 0),
        }));
        await apiPost(`/rfq/${rfqId}/quotes`, {
          vendor_id: quote.vendor_id,
          vendor_name: quote.vendor,
          line_items: lineItemsPayload,
          delivery_days: Number(quote.delivery_days || 0),
          payment_terms: quote.payment_terms,
          validity: quote.validity,
        });
      } else {
        await apiPost(`/rfq/${rfqId}/quotes`, {
          vendor_id: quote.vendor_id,
          vendor_name: quote.vendor,
          rate: Number(quote.rate || 0),
          delivery_days: Number(quote.delivery_days || 0),
          payment_terms: quote.payment_terms,
          validity: quote.validity,
        });
      }
      toast.success('Quote added');
      setQuoteOf(null); setQuote({}); setLineRates({});
      onReload();
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === 'object' ? (d?.message || 'Failed to add quote') : (d || 'Failed to add quote');
      toast.error(msg);
    }
  };

  // ── Normalize both legacy single-item and new multi-item shapes ──
  const rfqProject = (r) => r.project_name || r.project || '—';
  const rfqItemSummary = (r) => {
    if (r.line_items?.length) {
      if (r.line_items.length === 1) return `${r.line_items[0].item || r.line_items[0].material_name || '—'} · ${r.line_items[0].qty || r.line_items[0].quantity || '?'} ${r.line_items[0].uom || r.line_items[0].unit || ''}`.trim();
      return `${r.line_items.length} items`;
    }
    return r.item || '—';
  };
  const rfqQty = (r) => {
    // Round 4 P4 — always show total qty (never "N items" in the QTY column).
    if (r.line_items?.length) {
      const total = r.line_items.reduce((s, li) => s + (parseFloat(li.qty || li.quantity || 0) || 0), 0);
      const units = [...new Set(r.line_items.map(li => li.uom || li.unit).filter(Boolean))];
      return `${total.toLocaleString('en-IN')}${units.length === 1 ? ' ' + units[0] : ''}`;
    }
    return `${r.quantity || '?'} ${r.unit || ''}`.trim();
  };
  const rfqQtyTooltip = (r) => {
    if (!r.line_items?.length) return '';
    return r.line_items.map(li => `${li.material_name || li.item || '—'}: ${li.qty || li.quantity || 0} ${li.uom || li.unit || ''}`).join('\n');
  };
  const rfqLocked = (r) => {
    const st = (r.status || '').toString().toLowerCase();
    return st === 'awarded' || st === 'closed' || st === 'cancelled' || !!r.po_id;
  };
  const rfqCanQuote = (r) => {
    if (rfqLocked(r)) return false;
    const st = (r.status || '').toString().toLowerCase();
    if (st !== 'open' && st !== 'quoted') return false;
    // Must have invited vendors
    return (r.vendor_ids?.length || r.vendors?.length || 0) > 0;
  };
  const rfqVendors = (r) => {
    // New shape: vendor_ids + resolved vendor_names array
    if (Array.isArray(r.vendor_names) && r.vendor_names.length) return r.vendor_names.join(', ');
    // Legacy shape: vendors string array
    if (Array.isArray(r.vendors) && r.vendors.length) return r.vendors.join(', ');
    if (r.vendor_name) return r.vendor_name;
    return '—';
  };
  const rfqVendorCount = (r) => (r.vendor_ids?.length || r.vendors?.length || 0);

  const RFQ_STATUSES = ['Open', 'Quoted', 'Awarded', 'Cancelled'];

  return (
    <div>
      <div className="kpi-overline mb-3">Request For Quotation</div>
      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center mb-3">
        <input type="search" placeholder="Search RFQ no, project, vendor…"
          value={filterSearch} onChange={e => setFilterSearch(e.target.value)}
          className="border border-slate-300 rounded-sm px-3 py-1.5 text-xs focus:outline-none focus:border-[#3b4872] w-60"
          data-testid="rfq-filter-search" />
        <div className="flex items-center gap-1">
          {['', ...RFQ_STATUSES].map(s => (
            <button key={s || 'all'} onClick={() => setFilterStatus(s)}
              className={`text-[10px] px-2 py-0.5 rounded-full border font-display font-semibold transition-colors ${filterStatus === s ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'border-slate-200 text-slate-600 hover:border-[#3b4872]'}`}
              data-testid={`rfq-status-chip-${s || 'all'}`}>
              {s || 'All'}
            </button>
          ))}
        </div>
        {loading && <span className="text-[10px] text-slate-400">Loading…</span>}
      </div>
      <table className="w-full dense-table">
        <thead><tr><th>RFQ No</th><th>Project</th><th>Item / Summary</th><th className="text-right">Qty</th><th>Vendors</th><th className="text-center">Quotes</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {items.length === 0 && !loading && <tr><td colSpan="8" className="text-center py-12 text-slate-400 text-xs uppercase tracking-widest font-display">No RFQs · raise from an approved MPR</td></tr>}
          {items.map(r => (
            <tr key={r.id}>
              <td className="font-mono text-xs">{r.rfq_no}</td>
              <td>{rfqProject(r)}</td>
              <td>{rfqItemSummary(r)}</td>
              <td className="text-right tabular-nums font-display font-bold" title={rfqQtyTooltip(r)}>{rfqQty(r)}</td>
              <td className="text-xs max-w-[160px] truncate" title={rfqVendors(r)}>{rfqVendors(r)}</td>
              <td className="text-center font-display font-bold">{(r.quotes||[]).length}/{rfqVendorCount(r)}</td>
              <td>
                <div className="flex items-center gap-1.5">
                  <span className={`tag ${statusColor(r.status === 'Awarded' ? 'Approved' : 'Open')}`}>{r.status}</span>
                  {r.sourcing_route === 'emergency' && (
                    <span className={`px-1.5 py-0.5 text-[10px] rounded-sm font-display font-bold uppercase ${r.justification_approved_by ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}
                      title={r.justification_approved_by ? 'Emergency — justification approved' : 'Emergency — additional approval required before award'}
                      data-testid={`rfq-emergency-badge-${r.id}`}>
                      ⚡ Emergency{!r.justification_approved_by && ' · needs approval'}
                    </span>
                  )}
                </div>
              </td>
              <td>
                <div className="flex gap-2 items-center">
                  {rfqCanQuote(r) ? (
                    <>
                      <button data-testid="rfq-add-quote-btn" onClick={()=>{ setQuoteOf(r); setQuote({}); setLineRates({}); }} className="text-xs text-slate-500 hover:text-[#3b4872] font-display">Add Quote</button>
                      <button data-testid={`rfq-negotiate-btn-${r.id}`} onClick={()=>setNegotiating(r)} className="text-xs text-amber-700 hover:text-amber-800 font-display font-semibold">Negotiate{(r.negotiation_log||[]).length ? ` (${r.negotiation_log.length})` : ''}</button>
                    </>
                  ) : rfqLocked(r) ? (
                    <span className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-400" title={`RFQ ${r.status?.toLowerCase()} — quotes/negotiation are locked`} data-testid={`rfq-locked-${r.id}`}>Locked</span>
                  ) : (
                    <span className="text-[10px] font-display font-bold uppercase tracking-wider text-amber-600" title="Invite an empanelled vendor first">No vendors invited</span>
                  )}
                  <button data-testid="rfq-view-cs-btn" onClick={()=>onView(r.id)} className="text-xs text-[#3b4872] font-display font-semibold">View CS</button>
                  <button data-testid={`rfq-history-btn-${r.id}`} onClick={() => setHistoryRfq({ id: r.id, rfq_no: r.rfq_no })} title="Rate revision history" className="text-slate-400 hover:text-[#3b4872]"><History size={13} /></button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {total > limit && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <Pagination
            page={page} pageSize={limit} total={total}
            start={(page - 1) * limit} end={Math.min(page * limit, total)}
            onPageChange={setPage} onPageSizeChange={setLimit}
            pageSizeOptions={[20, 50, 100]} label="RFQs"
            data-testid="rfq-pagination"
          />
        </div>
      )}

      {quoteOf && (() => {
        const isMultiItem = (quoteOf?.line_items?.length || 0) > 1;
        const grandTotal = isMultiItem
          ? quoteOf.line_items.reduce((sum, li, idx) => sum + (parseFloat(lineRates[li.id || li.material_id || idx] || 0) * parseFloat(li.qty || li.quantity || 0)), 0)
          : 0;
        return (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <form onSubmit={submit} onClick={e=>e.stopPropagation()} className={`bg-white rounded-sm p-5 w-full ${isMultiItem ? 'max-w-2xl' : 'max-w-md'} grid grid-cols-2 gap-4`}>
              <div className="col-span-2">
                <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872]">New</div>
                <h3 className="font-display font-bold text-xl mt-1">Vendor Quote</h3>
              </div>

              {/* Vendor field — VendorPicker (empanelled-only) — Round 4 P1 */}
              <div className="col-span-2">
                <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">
                  Vendor <span className="text-red-500">*</span>
                </label>
                <VendorPicker
                  value={quote.vendor_id || ''}
                  displayName={quote.vendor || ''}
                  onSelect={(v) => setQuote({ ...quote, vendor_id: v?.id || '', vendor: v?.name || '' })}
                  placeholder="Pick an empanelled vendor…"
                  testid="quote-vendor-picker"
                />
                <p className="text-[10px] text-slate-400 mt-1">Only approved vendors on this RFQ's invite list can quote. Pending vendors show a "Request empanelment" link.</p>
              </div>

              {isMultiItem ? (
                /* Multi-item: per-line rate table */
                <div className="col-span-2">
                  <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-2">Line Item Rates <span className="text-red-500">*</span></div>
                  <table className="w-full text-xs border border-slate-200 rounded-sm overflow-hidden">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-display font-semibold text-slate-600">Material</th>
                        <th className="px-3 py-2 text-right font-display font-semibold text-slate-600">Qty</th>
                        <th className="px-2 py-2 text-left font-display font-semibold text-slate-600">UOM</th>
                        <th className="px-3 py-2 text-right font-display font-semibold text-slate-600">Rate (₹) <span className="text-red-500">*</span></th>
                        <th className="px-3 py-2 text-right font-display font-semibold text-slate-600">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quoteOf.line_items.map((li, idx) => {
                        const itemKey = li.id || li.material_id || idx;
                        const rate = parseFloat(lineRates[itemKey] || 0);
                        const qty = parseFloat(li.qty || li.quantity || 0);
                        const subtotal = rate * qty;
                        return (
                          <tr key={itemKey} className="border-t border-slate-100">
                            <td className="px-3 py-2 font-medium">{li.material_name || li.item || '—'}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{qty}</td>
                            <td className="px-2 py-2 text-slate-500">{li.uom || li.unit || '—'}</td>
                            <td className="px-3 py-2">
                              <input type="number" min="0" step="any"
                                value={lineRates[itemKey] || ''}
                                onChange={e => setLineRates(prev => ({ ...prev, [itemKey]: e.target.value }))}
                                className="w-28 px-2 py-1 border border-slate-300 rounded-sm text-xs text-right focus:border-[#3b4872] focus:outline-none"
                                placeholder="0.00" />
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums font-display font-semibold">
                              {subtotal > 0 ? `₹${subtotal.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}` : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                      <tr>
                        <td colSpan="4" className="px-3 py-2 text-right font-display font-bold text-sm text-slate-700">Grand Total</td>
                        <td className="px-3 py-2 text-right tabular-nums font-display font-bold text-sm text-[#3b4872]">
                          {grandTotal > 0 ? `₹${grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}` : '—'}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                /* Single-item: flat rate input */
                <div>
                  <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">
                    Rate <span className="text-red-500">*</span>
                  </label>
                  <input type="number" min="0" step="any" value={quote.rate||''} onChange={e=>setQuote({...quote, rate: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none" />
                </div>
              )}

              {/* Common RFQ-level fields */}
              <div>
                <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">
                  Delivery Days <span className="text-red-500">*</span>
                </label>
                <input type="number" min="0" value={quote.delivery_days||''} onChange={e=>setQuote({...quote, delivery_days: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none" />
              </div>
              <div>
                <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">
                  Payment Terms <span className="text-red-500">*</span>
                </label>
                <input type="text" value={quote.payment_terms||''} onChange={e=>setQuote({...quote, payment_terms: e.target.value})}
                  placeholder="e.g. 30 days"
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none" />
              </div>
              <div>
                <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">
                  Validity <span className="text-red-500">*</span>
                </label>
                <input type="text" value={quote.validity||''} onChange={e=>setQuote({...quote, validity: e.target.value})}
                  placeholder="e.g. 15 days"
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none" />
              </div>

              <div className="col-span-2 flex justify-end gap-2 pt-3 border-t border-slate-200">
                <button type="button" onClick={()=>{ setQuoteOf(null); setQuote({}); setLineRates({}); }} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50 font-display">Cancel</button>
                <button type="submit" data-testid="quote-submit-btn" className="px-4 py-2 text-sm bg-[#3b4872] text-white rounded-sm font-display font-semibold">Add Quote</button>
              </div>
            </form>
          </div>
        );
      })()}
      {negotiating && (
        <RFQNegotiationDrawer rfq={negotiating} onClose={() => setNegotiating(null)} onChanged={onReload} />
      )}
      {historyRfq && (
        <QuoteHistoryModal
          rfqId={historyRfq.id}
          rfqNo={historyRfq.rfq_no}
          vendorFilter={null}
          onClose={() => setHistoryRfq(null)}
        />
      )}
    </div>
  );
};

// ----- Comparative Statement -----
// Resend Invites — Procurement-only, visible while RFQ has no committee_decision
function ResendInvitesButton({ rfqId }) {
  const [sending, setSending] = React.useState(false);
  const resend = async () => {
    setSending(true);
    try {
      const d = await apiPost(`/rfq/${rfqId}/committee-resend`, {});
      toast.success(`Invites resent to ${d.sent} member(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Resend failed'); }
    finally { setSending(false); }
  };
  return (
    <button onClick={resend} disabled={sending} data-testid="committee-resend-btn"
      className="text-xs text-slate-500 underline hover:text-[#3b4872] disabled:opacity-50">
      {sending ? 'Sending…' : 'Resend invites'}
    </button>
  );
}

// Item n (Emergent_Procurement doc) — Procurement picks the committee for
// THIS RFQ from the global pool; that RFQ's committee then votes on it only.
function CommitteeMembersPicker({ rfqId, selected, onSaved }) {
  const [open, setOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  // Item 11 (Fixes_Combined): the picker lists ACTIVE employees with an ERP
  // login, not the fixed global committee pool.
  const [emps, setEmps] = React.useState([]);
  const [q, setQ] = React.useState('');
  const keyOf = (m) => m.user_id || m.email || m.name;
  const [picked, setPicked] = React.useState(() => new Map((selected || []).map(m => [keyOf(m), m])));
  React.useEffect(() => {
    if (!open) return;
    apiGet('/employees/lookup', { limit: 100, q: q || undefined })
      .then(r => setEmps(r?.items || r || []))
      .catch(() => setEmps([]));
  }, [open, q]);
  const toggle = (m) => {
    const k = keyOf(m);
    const next = new Map(picked);
    next.has(k) ? next.delete(k) : next.set(k, m);
    setPicked(next);
  };
  const save = async () => {
    setSaving(true);
    try {
      const members = Array.from(picked.values()).map(m => ({
        kind: 'internal', user_id: m.user_id || undefined, email: m.email, name: m.name,
      }));
      await apiPut(`/rfq/${rfqId}/committee-members`, { members });
      toast.success('Committee set for this RFQ');
      onSaved && onSaved();
      setOpen(false);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'object' ? (d?.message || 'Could not save committee') : (d || 'Could not save committee'));
    } finally { setSaving(false); }
  };
  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="committee-pick-toggle"
        className="text-[11px] text-amber-800 underline mb-2 block">
        {selected && selected.length ? `Committee for this RFQ: ${selected.length} member(s) — edit` : 'Pick committee members for this RFQ'}
      </button>
    );
  }
  return (
    <div className="mb-3 bg-white border border-amber-200 rounded-sm p-3" data-testid="committee-pick-panel">
      <p className="text-[11px] text-slate-500 mb-2">Choose who sits on the committee for THIS RFQ. Only active employees with an ERP login can be picked.</p>
      <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search employees…"
        className="w-full mb-2 px-2 py-1.5 border border-slate-300 rounded-sm text-xs" data-testid="committee-pick-search" />
      {picked.size > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {Array.from(picked.values()).map((m, i) => (
            <span key={i} className="text-[11px] bg-indigo-50 text-[#3b4872] px-2 py-0.5 rounded-sm">
              {m.name || m.email}
              <button onClick={() => toggle(m)} className="ml-1 text-slate-400">×</button>
            </span>
          ))}
        </div>
      )}
      <div className="max-h-48 overflow-y-auto border border-slate-100 rounded-sm divide-y mb-3">
        {emps.map((m, i) => (
          <label key={keyOf(m) || i} className="flex items-center gap-2 text-xs px-2 py-1.5 cursor-pointer" data-testid={`committee-pick-opt-${i}`}>
            <input type="checkbox" checked={picked.has(keyOf(m))} onChange={() => toggle(m)} />
            <span className="truncate">{m.name}{m.email ? <span className="text-slate-400"> · {m.email}</span> : null}</span>
          </label>
        ))}
        {emps.length === 0 && <div className="px-2 py-3 text-[11px] text-slate-400">No matching active employees.</div>}
      </div>
      <div className="flex gap-2">
        <button onClick={save} disabled={saving} data-testid="committee-pick-save"
          className="text-xs px-3 py-1.5 bg-[#3b4872] text-white rounded-sm font-display font-semibold disabled:opacity-50">
          {saving ? 'Saving…' : 'Save Committee'}
        </button>
        <button onClick={() => setOpen(false)} className="text-xs px-3 py-1.5 text-slate-500">Cancel</button>
      </div>
    </div>
  );
}


const CSPanel = ({ rfqId, setRfqId, onReload, refreshKey, setTab }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [rfqList, setRfqList] = useState([]);
  const [selVendor, setSelVendor] = useState('');
  const [rationale, setRationale] = useState('');
  const [savingDecision, setSavingDecision] = useState(false);
  const [awardTarget, setAwardTarget] = useState(null);   // Round 4 P4 — ConfirmDialog for Award
  const [awarding, setAwarding] = useState(false);
  const [tcSets, setTcSets] = useState([]);
  // Items 2/3 (Fixes_Combined): extra clause + ship-to on the Award modal.
  const [awardExtra, setAwardExtra] = useState({ tc_set_id: '', extra_clause: '', ship_to: '', charges: [] });
  const [itemwise, setItemwise] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;

  // Fetch RFQ list — for both the dropdown selector and the CS-list mode.
  useEffect(() => {
    apiGet('/rfq', { limit: 200 }).then(r => setRfqList(r?.items || [])).catch(() => {});
  }, [refreshKey]);

  // Items 3/7 (Emergent_Procurement doc): T&C sets for the award modal.
  useEffect(() => {
    apiGet('/settings/tc-library').then((d) => setTcSets(d?.sets || [])).catch(() => {});
  }, []);

  // Item 3 (Fixes_Combined): pre-fill the Award modal's ship-to from the project.
  // Followup round 2, P2: projects only hold `location` (no address/site_address).
  useEffect(() => {
    const pid = data?.rfq?.project_id;
    if (!awardTarget || !pid) return;
    apiGet(`/projects/${pid}`).then((p) => {
      const addr = p?.address || p?.site_address || p?.location || '';
      if (addr) setAwardExtra((x) => ({ ...x, ship_to: x.ship_to || addr }));
    }).catch(() => {});
  }, [awardTarget]);

  const [historyModal, setHistoryModal] = useState(null); // { vendorId }
  const [editQuote, setEditQuote] = useState(null);   // quote object being edited (pre-filled)
  const [editForm, setEditForm] = useState({});
  const [withdrawing, setWithdrawing] = useState(null); // quote object being withdrawn
  const [withdrawReason, setWithdrawReason] = useState('');

  const loadComparative = () => {
    if (!rfqId) return;
    setError(null);
    apiGet(`/rfq/${rfqId}/comparative`).then((d) => {
      setData(d);
      setSelVendor(d?.committee?.decision?.selected_vendor || '');
      setRationale('');
    }).catch((err) => {
      setData(null);
      setError(err?.response?.data?.detail || 'Failed to load comparative statement');
    });
  };

  useEffect(() => { loadComparative(); /* eslint-disable-next-line */ }, [rfqId]);

  // Round 4 P4 — CS tab defaults to a listable/printable index of RFQs with ≥1 quote.
  if (!rfqId) {
    const withQuotes = (rfqList || []).filter(r => (r.quotes?.length || 0) >= 1);
    return (
      <div data-testid="cs-list-view">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="kpi-overline">Comparative Statements</div>
            <h3 className="font-display font-bold text-base mt-1">{withQuotes.length} RFQ{withQuotes.length !== 1 ? 's' : ''} with quotes</h3>
          </div>
        </div>
        <table className="w-full dense-table">
          <thead><tr><th>RFQ No</th><th>Project</th><th>Item / Summary</th><th className="text-right">Quotes</th><th>L1 Vendor</th><th className="text-right">L1 Value</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {withQuotes.length === 0 && <tr><td colSpan="8" className="text-center py-12 text-slate-400 text-xs uppercase tracking-widest font-display">No RFQs with quotes yet — add a quote in the RFQ tab to see it here</td></tr>}
            {withQuotes.map(r => {
              const quotes = r.quotes || [];
              const totals = quotes.map(q => {
                if (q.line_items?.length) return q.line_items.reduce((s, li) => s + (parseFloat(li.rate || 0) * parseFloat(li.qty || 0)), 0);
                return parseFloat(q.total || 0) || (parseFloat(q.rate || 0) * parseFloat(r.quantity || 1));
              });
              const l1Idx = totals.length ? totals.indexOf(Math.min(...totals)) : -1;
              const l1 = l1Idx >= 0 ? quotes[l1Idx] : null;
              const l1Name = l1?.vendor_name || l1?.vendor || '—';
              const l1Val = l1Idx >= 0 ? totals[l1Idx] : 0;
              const item = r.line_items?.length === 1 ? (r.line_items[0].material_name || r.line_items[0].item || '—') : (r.line_items?.length > 1 ? `${r.line_items.length} items` : (r.item || '—'));
              return (
                <tr key={r.id || r.rfq_no} className="cursor-pointer hover:bg-slate-50" onClick={()=>setRfqId(r.id)} data-testid={`cs-list-row-${r.id}`}>
                  <td className="font-mono text-xs">{r.rfq_no || '—'}</td>
                  <td className="max-w-[180px] truncate" title={r.project_name || r.project}>{r.project_name || r.project || '—'}</td>
                  <td className="max-w-[220px] truncate" title={item}>{item}</td>
                  <td className="text-right tabular-nums font-display font-bold">{quotes.length}</td>
                  <td className="text-xs">{l1Name}</td>
                  <td className="text-right tabular-nums font-mono text-xs">{inr2(l1Val)}</td>
                  <td><span className={`tag ${statusColor(r.status === 'Awarded' ? 'Approved' : 'Open')}`}>{r.status}</span>
                    {r.po_id && <span className="ml-1.5 text-[10px] font-mono text-emerald-700">→ PO awarded</span>}
                  </td>
                  <td>
                    <button
                      onClick={(e) => { e.stopPropagation(); setRfqId(r.id); }}
                      className="text-xs text-[#3b4872] font-display font-semibold hover:underline"
                      data-testid={`cs-open-${r.id}`}
                    >Open</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }
  if (error) return (
    <div className="py-8 text-center">
      <p className="text-rose-600 text-sm mb-3">{error}</p>
      <button onClick={loadComparative} className="px-4 py-2 text-xs bg-[#3b4872] text-white rounded-sm font-display font-semibold" data-testid="cs-retry-btn">Retry</button>
    </div>
  );
  if (!data) return <div className="text-slate-400 text-sm py-8 text-center">Loading…</div>;

  const committee = data.committee || { threshold: 0, required: false, is_member: false, decision: null };
  const decision = committee.decision;
  const committeeRequired = committee.required;
  const fmtThreshold = inr(committee.threshold);

  const matchesDecision = (q) =>
    decision && [q.vendor, q.vendor_name, q.vendor_id].some(
      (k) => k && [decision.selected_vendor, decision.selected_vendor_name, decision.selected_vendor_id].includes(k)
    );

  const award = async (vendor) => {
    setAwarding(true);
    try {
      await apiPost(`/rfq/${rfqId}/award`, {
        vendor,
        tc_set_id: awardExtra.tc_set_id || undefined,
        extra_clause: awardExtra.extra_clause || undefined,
        ship_to: awardExtra.ship_to || undefined,
        charges: cleanCharges(awardExtra.charges),
      });
      toast.success(`PO created for ${vendor}`);
      onReload(); setTab('Purchase Orders');
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'object' ? (d?.message || 'Could not award PO') : (d || 'Could not award PO');
      toast.error(msg);
    } finally { setAwarding(false); setAwardTarget(null); }
  };

  const rfqAwarded = (data?.rfq?.status || '').toLowerCase() === 'awarded' || !!data?.rfq?.po_id;

  const openEditQuote = (q) => {
    setEditQuote(q);
    setEditForm({
      rate: q.rate ?? '', delivery_days: q.delivery_days ?? '',
      payment_terms: q.payment_terms ?? '', validity: q.validity ?? '',
      line_items: (q.line_items || []).map(li => ({ ...li })),
      edit_reason: '',
    });
  };
  const submitEditQuote = async (e) => {
    e.preventDefault();
    if (!editForm.edit_reason?.trim()) { toast.error('A reason is required to edit this quote'); return; }
    try {
      const isMulti = (editForm.line_items || []).length > 0;
      await apiPost(`/rfq/${rfqId}/quotes`, {
        vendor_id: editQuote.vendor_id, vendor_name: editQuote.vendor,
        ...(isMulti
          ? { line_items: editForm.line_items.map(li => ({ id: li.id, material_name: li.material_name, qty: li.qty, uom: li.uom, rate: parseFloat(li.rate || 0) })) }
          : { rate: parseFloat(editForm.rate || 0) }),
        delivery_days: Number(editForm.delivery_days || 0),
        payment_terms: editForm.payment_terms, validity: editForm.validity,
        edit_reason: editForm.edit_reason.trim(),
      });
      toast.success('Quote edited');
      setEditQuote(null); loadComparative(); onReload();
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      toast.error(typeof d === 'object' ? (d?.message || 'Could not edit quote') : (d || 'Could not edit quote'));
    }
  };
  const submitWithdraw = async () => {
    if (!withdrawReason.trim()) { toast.error('A reason is required to withdraw this quote'); return; }
    try {
      await apiPost(`/rfq/${rfqId}/quotes/${withdrawing.vendor_id}/withdraw`, { reason: withdrawReason.trim() });
      toast.success('Quote withdrawn');
      setWithdrawing(null); setWithdrawReason(''); loadComparative(); onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not withdraw quote');
    }
  };

  const downloadCsPdf = async () => {
    await fetchPdf(`/rfq/${rfqId}/comparative/pdf`, `CS_${pdfSafeName(data?.rfq?.rfq_no || rfqId)}.pdf`);
  };

  const recordDecision = async () => {
    if (!selVendor) { toast.error('Select the vendor the committee chose'); return; }
    if (!rationale.trim()) { toast.error('Add a short rationale for the committee decision'); return; }
    setSavingDecision(true);
    try {
      await apiPost(`/rfq/${rfqId}/committee-decision`, { selected_vendor: selVendor, rationale: rationale.trim() });
      toast.success('Committee decision recorded');
      loadComparative();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not record committee decision');
    } finally {
      setSavingDecision(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="kpi-overline">Comparative Statement</div>
          <h3 className="font-display font-bold text-base mt-1">{data.rfq.rfq_no} · {data.rfq.project_name || data.rfq.project || data.rfq.item || '—'}</h3>
          {(data.rfq.line_items || []).length > 1 && data.rfq.status !== 'Awarded' && (
            <button onClick={() => setItemwise(true)} className="mt-1 px-3 py-1.5 text-xs border border-[#3b4872] text-[#3b4872] rounded-sm font-semibold" data-testid="cs-award-itemwise-btn">Award item-wise</button>)}
          {data.rfq.status === 'Partially Awarded' && <span className="ml-2 text-xs text-amber-700" data-testid="cs-partially-awarded">Partially Awarded</span>}
          {itemwise && <ItemwiseAwardModal rfq={data.rfq} onClose={() => setItemwise(false)} onDone={() => { setItemwise(false); loadComparative(); onReload?.(); }} />}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setRfqId(null)}
            className="text-xs text-slate-500 hover:text-[#3b4872] font-display font-semibold px-2 py-1"
            data-testid="cs-back-to-list"
          >← Back to list</button>
          <button
            type="button"
            onClick={downloadCsPdf}
            className="text-xs px-3 py-1.5 border border-slate-300 rounded-sm hover:bg-slate-50 font-display font-semibold flex items-center gap-1"
            data-testid="cs-print-btn"
          ><Download size={12} /> Print PDF</button>
          <select value={rfqId} onChange={e=>setRfqId(e.target.value)} className="px-3 py-1.5 border border-slate-300 rounded-sm text-xs bg-white">
            {rfqList.map(r => { const nm = r.project_name || r.project || r.item || (r.line_items?.[0]?.item) || ''; return <option key={r.id} value={r.id}>{r.rfq_no}{nm ? ` — ${nm}` : ''}</option>; })}
          </select>
        </div>
      </div>

      {rfqAwarded && (
        <div className="mb-4 border border-emerald-200 bg-emerald-50/60 rounded-sm p-3 text-sm text-emerald-800 flex items-center justify-between" data-testid="cs-awarded-banner">
          <span><Trophy size={14} className="inline -mt-0.5 mr-1" /> Awarded to <b>{data.rfq.awarded_to || '—'}</b>. Quotes and Award are now locked for this RFQ.</span>
          {data.rfq.po_id && (
            <button onClick={() => setTab('Purchase Orders')} className="text-xs font-display font-semibold text-[#3b4872] hover:underline" data-testid="cs-jump-to-po">View PO →</button>
          )}
        </div>
      )}

      {/* Committee vendor-selection (above-threshold orders only) */}
      {committeeRequired && (
        <div data-testid="committee-panel" className="mb-4 border border-amber-300 bg-amber-50/60 rounded-sm p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users size={15} className="text-amber-700" />
            <span className="font-display font-bold text-sm text-amber-900">Committee Vendor Selection required</span>
            <span className="text-[11px] text-amber-700">(order above {fmtThreshold})</span>
          </div>
          {!decision && (
            <CommitteeMembersPicker rfqId={rfqId} selected={data.rfq?.committee_members} onSaved={loadComparative} />
          )}
          {decision ? (
            <div data-testid="committee-decision-recorded" className="text-sm text-slate-700">
              <div className="font-semibold text-emerald-800">Committee selected: {decision.selected_vendor_name}</div>
              <div className="text-xs mt-1 text-slate-600 italic">“{decision.rationale}”</div>
              <div className="text-[11px] mt-1 text-slate-500">
                Recorded by {decision.decided_by} · {formatDate(decision.decided_at)}
              </div>
            </div>
          ) : committee.is_member ? (
            <div className="space-y-2">
              <p className="text-xs text-amber-800">As a committee member, record which vendor was selected from the comparative below, with a rationale. The order can only be awarded to the selected vendor.</p>
              <div className="grid md:grid-cols-2 gap-2">
                <select
                  data-testid="committee-vendor-select"
                  value={selVendor}
                  onChange={e=>setSelVendor(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white"
                >
                  <option value="">Select chosen vendor…</option>
                  {data.comparative.map(q => (
                    <option key={q.vendor} value={q.vendor}>{q.rank} · {q.vendor} · {inr2(q.total_value)}</option>
                  ))}
                </select>
                <input
                  data-testid="committee-rationale"
                  value={rationale}
                  onChange={e=>setRationale(e.target.value)}
                  placeholder="Rationale (e.g. best delivery + compliance)"
                  className="px-3 py-2 border border-slate-300 rounded-sm text-sm"
                />
              </div>
              <button
                data-testid="committee-record-btn"
                onClick={recordDecision}
                disabled={savingDecision}
              className="text-xs px-3 py-1.5 bg-[#3b4872] text-white rounded-sm hover:bg-[#2d3856] font-display font-semibold disabled:opacity-50"
              >
                {savingDecision ? 'Saving…' : 'Record Committee Decision'}
              </button>
            </div>
          ) : (
            <div>
              {(data.committee?.members?.length || 0) === 0 ? (
                <p className="text-xs text-amber-800" data-testid="committee-no-members-msg">
                  No committee members have been picked for this RFQ yet — select members above before invites can be sent.
                </p>
              ) : (
                <>
                  <p className="text-xs text-amber-800">Awaiting the selection committee to record their chosen vendor. Invites have been emailed.</p>
                  <ResendInvitesButton rfqId={rfqId} />
                </>
              )}
            </div>
          )}
        </div>
      )}

      <table className="w-full dense-table">
        <thead><tr>
          <th>Rank</th><th>Vendor</th><th className="text-right">Rate / Items</th><th className="text-right">Total Value</th>
          <th>Delivery</th><th>Terms</th><th>Approver</th><th></th>
        </tr></thead>
        <tbody>
          {data.comparative.length === 0 && <tr><td colSpan="8" className="text-center py-12 text-slate-400 text-xs uppercase tracking-widest font-display">No quotes received yet</td></tr>}
          {data.comparative.map((q, i) => {
            const isWithdrawn = q.rank === 'Withdrawn';
            const isSelected = !isWithdrawn && matchesDecision(q);
            // Below threshold → award L1 directly (as before). Above threshold →
            // award only the committee-selected vendor, and only after a decision.
            const showAward = !isWithdrawn && (committeeRequired ? (decision && isSelected) : (i === 0));
            const blockedNoDecision = !isWithdrawn && committeeRequired && !decision && i === 0;
            const isMultiItem = (q.line_items?.length || 0) > 1;
            return (
              <React.Fragment key={q.vendor}>
                <tr className={isWithdrawn ? 'opacity-50' : isSelected ? 'bg-amber-50/50' : i === 0 ? 'bg-emerald-50/30' : ''} data-testid={isWithdrawn ? `cs-row-withdrawn-${i}` : undefined}>
                  <td>
                    <span className={`tag ${isWithdrawn ? 'bg-rose-50 text-rose-600 border border-rose-200' : i===0?'bg-emerald-50 text-emerald-700 border border-emerald-200':'bg-slate-100 text-slate-600 border border-slate-200'} flex items-center gap-1 w-fit`}>
                      {!isWithdrawn && i === 0 && <Trophy size={11} />}{q.rank}
                    </span>
                  </td>
                  <td className={`font-display font-bold ${isWithdrawn ? 'line-through text-slate-400' : ''}`}>
                    {q.vendor}
                    {q.edited && !isWithdrawn && <span className="ml-2 tag bg-blue-50 text-blue-700 border border-blue-200" title={q.edit_reason}>Edited</span>}
                    {q.performance_band && <span className="ml-2"><VendorBandBadge band={q.performance_band} /></span>}
                    {isSelected && <span className="ml-2 tag bg-amber-100 text-amber-800 border border-amber-200">Committee pick</span>}
                    {isWithdrawn && <div className="text-[10px] text-rose-500 font-normal mt-0.5">Withdrawn — {q.withdraw_reason}</div>}
                  </td>
                  <td className={`text-right tabular-nums font-mono ${isWithdrawn ? 'line-through text-slate-400' : ''}`}>
                    {isMultiItem ? <span className="text-slate-400 text-[10px]">{q.line_items.length} items ↓</span> : inr2(q.rate)}
                  </td>
                  <td className={`text-right tabular-nums font-display font-bold ${isWithdrawn ? 'line-through text-slate-400' : ''}`}>{inr2(q.total_value)}</td>
                  <td className="text-xs">{q.delivery_days} days</td>
                  <td className="text-xs text-slate-500">{q.payment_terms}</td>
                  <td className="text-xs">
                    <span className="font-display font-semibold">{q.approver_required}</span>
                  </td>
                  <td>
                    {rfqAwarded ? (
                      matchesDecision(q) || i === 0 ? (
                        <span className="text-[10px] font-display font-bold uppercase tracking-wider text-emerald-700" data-testid={`cs-row-awarded-${i}`}>Awarded ✓</span>
                      ) : null
                    ) : (
                      <>
                        {showAward && (
                          <button data-testid="award-po-btn" onClick={()=>setAwardTarget({ vendor: q.vendor, total: q.total_value })} className="text-xs px-3 py-1.5 bg-[#3b4872] text-white rounded-sm hover:bg-[#2c3658] font-display font-semibold">Award PO</button>
                        )}
                        {blockedNoDecision && (
                          <button
                            data-testid="award-po-disabled"
                            disabled
                            title={`Committee must select the vendor for orders above ${fmtThreshold}`}
                            className="text-xs px-3 py-1.5 bg-slate-200 text-slate-400 rounded-sm font-display font-semibold cursor-not-allowed"
                          >
                            Award PO
                          </button>
                        )}
                        {!isWithdrawn && (
                          <>
                            <button onClick={() => openEditQuote(q)} data-testid={`cs-edit-quote-${i}`}
                              className="ml-1 text-xs px-2 py-1.5 text-slate-500 hover:text-[#3b4872] font-display font-semibold">Edit</button>
                            <button onClick={() => { setWithdrawing(q); setWithdrawReason(''); }} data-testid={`cs-withdraw-quote-${i}`}
                              className="ml-1 text-xs px-2 py-1.5 text-rose-500 hover:text-rose-700 font-display font-semibold">Withdraw</button>
                          </>
                        )}
                      </>
                    )}
                    <button
                      onClick={() => setHistoryModal({ vendorId: q.vendor_id || q.vendor })}
                      title="View this vendor's rate revision history"
                      className="ml-1 text-slate-400 hover:text-[#3b4872]"
                      data-testid={`cs-history-btn-${i}`}>
                      <History size={13} />
                    </button>
                  </td>
                </tr>
                {/* Per-line breakdown row for multi-item quotes */}
                {isMultiItem && (
                  <tr className="bg-slate-50/80">
                    <td></td>
                    <td colSpan="7" className="px-3 pb-2">
                      <table className="w-full text-[11px] text-slate-600">
                        <thead>
                          <tr className="text-[10px] text-slate-400 uppercase">
                            <th className="text-left py-1 font-display font-semibold">Material</th>
                            <th className="text-right py-1 font-display font-semibold">Qty</th>
                            <th className="text-left py-1 font-display font-semibold pl-2">UOM</th>
                            <th className="text-right py-1 font-display font-semibold">Rate</th>
                            <th className="text-right py-1 font-display font-semibold">Subtotal</th>
                          </tr>
                        </thead>
                        <tbody>
                          {q.line_items.map((li, idx) => (
                            <tr key={li.id || idx} className="border-t border-slate-100">
                              <td className="py-0.5">{li.material_name || li.item || '—'}</td>
                              <td className="py-0.5 text-right tabular-nums">{li.qty}</td>
                              <td className="py-0.5 text-slate-400 pl-2">{li.uom || '—'}</td>
                              <td className="py-0.5 text-right tabular-nums">{inr2(li.rate)}</td>
                              <td className="py-0.5 text-right tabular-nums font-semibold">{inr2(li.subtotal)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {/* All-vendor history button */}
      <div className="mt-3 flex justify-end">
        <button onClick={() => setHistoryModal({ vendorId: null })}
          className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#3b4872] font-display font-semibold"
          data-testid="cs-all-history-btn">
          <History size={12} /> View Full Rate History
        </button>
      </div>

      {historyModal && (
        <QuoteHistoryModal
          rfqId={rfqId}
          rfqNo={data.rfq.rfq_no}
          vendorFilter={historyModal.vendorId}
          onClose={() => setHistoryModal(null)}
        />
      )}

      {/* Items 4/7/8 (Emergent_Procurement doc) — Award modal with T&C set + freight/loading/other charges */}
      {awardTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="award-modal">
          <div className="bg-white rounded-sm shadow-xl w-full max-w-md p-5">
            <h3 className="font-display font-bold text-base text-slate-800 mb-1">Award PO?</h3>
            <p className="text-xs text-slate-500 mb-4">
              Create a Purchase Order for {awardTarget.vendor}{awardTarget.total ? ` (${inr2(awardTarget.total)})` : ''}?
              This will lock the RFQ and issue a Pending PO for approval.
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-display block mb-1">Terms &amp; Conditions Set</label>
                <select data-testid="award-tc-set-select" value={awardExtra.tc_set_id}
                  onChange={e => setAwardExtra({ ...awardExtra, tc_set_id: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
                  <option value="">None</option>
                  {tcSets.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-display block mb-1">Extra Clause (optional)</label>
                <input data-testid="award-extra-clause-input" value={awardExtra.extra_clause}
                  onChange={e => setAwardExtra({ ...awardExtra, extra_clause: e.target.value })}
                  placeholder="One additional clause for this PO only"
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 font-display block mb-1">Ship To / Delivery Address</label>
                <textarea data-testid="award-ship-to-input" rows={2} value={awardExtra.ship_to}
                  onChange={e => setAwardExtra({ ...awardExtra, ship_to: e.target.value })}
                  placeholder="Pre-filled from the project — edit if the material ships elsewhere"
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              </div>
              <ChargesTable charges={awardExtra.charges || []} onChange={c => setAwardExtra({ ...awardExtra, charges: c })}
                items={[{ gst_rate: Math.max(18, ...((data.rfq.line_items || []).map(l => Number(l.gst_rate) || 0))) }]} testid="award-charges" />
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setAwardTarget(null)} data-testid="award-modal-cancel" className="px-4 py-2 text-xs font-display font-semibold text-slate-600 hover:text-slate-800">Cancel</button>
              <button onClick={() => award(awardTarget.vendor)} disabled={awarding} data-testid="award-modal-confirm"
                className="px-4 py-2 text-xs font-display font-semibold bg-[#3b4872] text-white rounded-sm hover:bg-[#2c3658] disabled:opacity-50">
                {awarding ? 'Awarding…' : 'Award PO'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Procurement_7Sep2026 §2: Withdraw quote — mandatory reason, no hard delete */}
      {withdrawing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="withdraw-quote-modal">
          <div className="bg-white rounded-lg p-5 w-full max-w-md space-y-3">
            <h3 className="font-display font-bold text-slate-800">Withdraw {withdrawing.vendor}'s Quote</h3>
            <p className="text-xs text-slate-500">The quote is kept (struck-through) and excluded from ranking — it is not deleted.</p>
            <textarea autoFocus value={withdrawReason} onChange={(e) => setWithdrawReason(e.target.value)}
              placeholder="Reason for withdrawal (required)" rows={3}
              className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm" data-testid="withdraw-reason-input" />
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setWithdrawing(null)} className="text-xs px-3 py-2 text-slate-500" data-testid="withdraw-cancel-btn">Cancel</button>
              <button onClick={submitWithdraw} className="text-xs px-4 py-2 bg-rose-600 text-white rounded-sm font-display font-semibold" data-testid="withdraw-confirm-btn">Withdraw Quote</button>
            </div>
          </div>
        </div>
      )}

      {/* Procurement_7Sep2026 §2: Edit quote — pre-filled, mandatory reason, old version kept */}
      {editQuote && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" data-testid="edit-quote-modal">
          <form onSubmit={submitEditQuote} className="bg-white rounded-lg p-5 w-full max-w-md space-y-3 max-h-[90vh] overflow-y-auto">
            <h3 className="font-display font-bold text-slate-800">Edit {editQuote.vendor}'s Quote</h3>
            <p className="text-xs text-slate-500">The current version is kept in history — this creates a new "Edited" version.</p>
            {(editForm.line_items || []).length > 0 ? (
              <div className="space-y-2">
                {editForm.line_items.map((li, idx) => (
                  <div key={li.id || idx} className="flex items-center gap-2">
                    <span className="text-xs flex-1 truncate">{li.material_name}</span>
                    <input type="number" step="0.01" value={li.rate}
                      onChange={(e) => setEditForm(f => ({ ...f, line_items: f.line_items.map((x, i2) => i2 === idx ? { ...x, rate: e.target.value } : x) }))}
                      className="w-24 border border-slate-200 rounded-sm px-2 py-1 text-sm text-right" data-testid={`edit-quote-line-rate-${idx}`} />
                  </div>
                ))}
              </div>
            ) : (
              <label className="text-xs text-slate-600 block">Rate
                <input type="number" step="0.01" value={editForm.rate}
                  onChange={(e) => setEditForm(f => ({ ...f, rate: e.target.value }))}
                  className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-1" data-testid="edit-quote-rate-input" />
              </label>
            )}
            <label className="text-xs text-slate-600 block">Delivery (days)
              <input type="number" value={editForm.delivery_days}
                onChange={(e) => setEditForm(f => ({ ...f, delivery_days: e.target.value }))}
                className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-1" data-testid="edit-quote-delivery-input" />
            </label>
            <label className="text-xs text-slate-600 block">Payment Terms
              <input value={editForm.payment_terms}
                onChange={(e) => setEditForm(f => ({ ...f, payment_terms: e.target.value }))}
                className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-1" data-testid="edit-quote-terms-input" />
            </label>
            <label className="text-xs text-slate-600 block">Reason for edit (required)
              <textarea autoFocus value={editForm.edit_reason} rows={2}
                onChange={(e) => setEditForm(f => ({ ...f, edit_reason: e.target.value }))}
                className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mt-1" data-testid="edit-quote-reason-input" />
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setEditQuote(null)} className="text-xs px-3 py-2 text-slate-500" data-testid="edit-quote-cancel-btn">Cancel</button>
              <button type="submit" className="text-xs px-4 py-2 bg-[#3b4872] text-white rounded-sm font-display font-semibold" data-testid="edit-quote-save-btn">Save Edited Quote</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

// ----- PO Panel -----
const POPanel = ({ onReload, refreshKey, onOpenWizard, activeProject, matrix }) => {
  const { user } = useAuth();
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [notesPo, setNotesPo] = useState(null);
  const [detailPo, setDetailPo] = useState(null);
  const openReqRef = useRef(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [execReasonPrompt, setExecReasonPrompt] = useState(null); // { poId, fromDrawer } | null
  const [execReasonText, setExecReasonText] = useState('');
  const [execReasonBusy, setExecReasonBusy] = useState(false);
  // Server-side state
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const searchTimer = useRef(null);
  const API = process.env.REACT_APP_BACKEND_URL;

  // Debounce search
  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 300);
    return () => clearTimeout(searchTimer.current);
  }, [search]);
  useEffect(() => { setPage(1); }, [statusFilter]);

  const [pdfBusy, setPdfBusy] = useState(null);
  const [selfApprovedOnly, setSelfApprovedOnly] = useState(false);
  // Server-side data fetch
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { page, limit };
    if (debouncedSearch) params.q = debouncedSearch;
    if (statusFilter) params.status = statusFilter;
    if (selfApprovedOnly) params.self_approved = true;
    apiGet('/po', params).then(r => {
      if (!cancelled) { setItems(r?.items || []); setTotal(r?.total || 0); }
    }).catch(() => {}).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, limit, debouncedSearch, statusFilter, refreshKey, selfApprovedOnly]);

  const approve = async (e, id) => {
    e.stopPropagation();
    try {
      await apiPost(`/po/${id}/approve`, {});
      toast.success('PO approved'); onReload();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === 'EXEC_OVERRIDE_REASON_REQUIRED') {
        setExecReasonPrompt({ poId: id });
      } else {
        toast.error(detail?.message || detail || 'Approval failed');
      }
    }
  };

  const submitExecReason = async () => {
    if (!execReasonText.trim()) { toast.error('A reason is required'); return; }
    setExecReasonBusy(true);
    try {
      await apiPost(`/po/${execReasonPrompt.poId}/approve`, { exec_override_reason: execReasonText.trim() });
      toast.success('PO approved (exec override)');
      onReload();
      if (execReasonPrompt.fromDrawer) setDetailPo(null);
      setExecReasonPrompt(null); setExecReasonText('');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail?.message || detail || 'Approval failed');
    } finally { setExecReasonBusy(false); }
  };

  const openDetail = async (po) => {
    openReqRef.current = po.id;
    setDetailLoading(true);
    setDetailPo({ ...po, _loading: true });
    try {
      const full = await apiGet(`/po/${po.id}`);
      if (openReqRef.current === po.id) setDetailPo(full);
    } catch { toast.error('Failed to load PO details'); setDetailPo(null); }
    finally { setDetailLoading(false); }
  };
  const closeDetail = () => { openReqRef.current = null; setDetailPo(null); };

  const downloadPdf = async (e, po) => {
    e?.stopPropagation?.();
    if (pdfBusy) return;
    setPdfBusy(po.id);
    const vendor = (po.vendor_name || po.vendor || 'vendor').replace(/[^a-zA-Z0-9]/g, '_').slice(0, 20);
    await fetchPdf(`/po/${po.id}/pdf`, `PO_${pdfSafeName(po.po_no)}_${vendor}.pdf`);
    setPdfBusy(null);
  };

  // Server results — no client-side filter needed
  const STATUS_OPTIONS = ['Pending', 'Approved', 'Partially Received', 'Closed', 'Cancelled'];

  return (
    <div>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {/* Search */}
          <div className="relative flex-1 max-w-xs">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text" value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search PO no, vendor, project…"
              className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-sm focus:outline-none focus:border-[#3b4872]"
              data-testid="po-search-input"
            />
          </div>
          {/* Status chips */}
          <div className="flex items-center gap-1 flex-wrap">
            {['', ...STATUS_OPTIONS].map(s => (
              <button key={s || 'all'} onClick={() => setStatusFilter(s)}
                className={`text-[10px] px-2 py-0.5 rounded-full border font-display font-semibold transition-colors ${statusFilter === s ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'border-slate-200 text-slate-600 hover:border-[#3b4872]'}`}
                data-testid={`po-status-chip-${s || 'all'}`}>
                {s || 'All'}
              </button>
            ))}
            <button onClick={() => setSelfApprovedOnly(v => !v)} data-testid="po-self-approved-filter"
              className={`text-[10px] px-2 py-0.5 rounded-full border font-display font-semibold transition-colors ${selfApprovedOnly ? 'bg-amber-500 text-white border-amber-500' : 'border-amber-300 text-amber-700 hover:border-amber-500'}`}>
              Self-approved
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setImporting(true)} data-testid="po-bulk-upload-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-[#3b4872] text-[#3b4872] hover:bg-indigo-50 rounded-sm font-display font-semibold">
            <Upload size={13} /> Bulk Upload
          </button>
          <button onClick={onOpenWizard} data-testid="po-wizard-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#3b4872] hover:bg-[#2c3658] text-white rounded-sm font-display font-semibold">
            <Wand2 size={13} /> New PO (Wizard)
          </button>
          <button onClick={() => setCreating(true)} data-testid="manual-po-new-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-slate-300 hover:border-[#3b4872] text-slate-600 rounded-sm font-display font-semibold">
            <Plus size={13} /> Manual
          </button>
        </div>
      </div>

      {/* Table */}
      <table className="w-full dense-table">
        <thead>
          <tr>
            <th>PO No</th><th>Vendor</th><th>Project</th>
            <th className="text-right">Value</th><th>Approver</th><th>Status</th><th></th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && !loading && <tr><td colSpan="7" className="text-center py-12 text-slate-400 text-xs uppercase tracking-widest font-display">No POs found</td></tr>}
          {items.map(p => (
            <tr key={p.id} onClick={() => openDetail(p)}
              className="cursor-pointer hover:bg-indigo-50/40 transition-colors"
              data-testid={`po-row-${p.id}`}>
              <td className="font-mono text-xs" onClick={e => e.stopPropagation()}>
                {p.po_no}
                {p.is_direct_po && (
                  <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-700 border border-amber-200" title={p.bypass_reason || 'No RFQ — direct PO'}>
                    Direct PO
                  </span>
                )}
                {p.self_approved && (
                  <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-700 border border-amber-200"
                    title={p.self_approved_basis === 'procurement_group'
                      ? `Self-approved by ${p.approved_by || ''} (Procurement team · ${p.self_approved_department || p.self_approved_role || ''})`
                      : p.exec_override
                      ? `Exec override by ${p.exec_override_by_name || p.approved_by || ''} — ${p.exec_override_reason || '(no reason)'}`
                      : `Self-approved by ${p.approved_by || ''} (${p.self_approved_role || 'exec'})`}
                    data-testid={`po-self-approved-${p.id}`}>
                    {p.self_approved_basis === 'procurement_group' ? 'Self-approved (Procurement team)'
                      : p.exec_override ? `Exec override · ${p.exec_override_by_name || p.approved_by || ''}` : 'Self-approved'}
                  </span>
                )}
              </td>
              <td className="text-sm">{p.vendor || p.vendor_name}</td>
              <td className="text-xs text-slate-600" title={p.is_multi_project ? (p.ship_to_blocks || []).map(b => b.project_name).join(', ') : undefined} data-testid={`po-project-cell-${p.id}`}>
                {p.is_multi_project ? `Multiple (${(p.project_ids || []).length})` : (p.project_name || p.project)}
                {Number(p.revision) > 0 && <span className="ml-1 text-[9px] px-1 rounded bg-indigo-50 text-[#3b4872]">Rev {p.revision}</span>}</td>
              <td className="text-right tabular-nums font-display font-bold">{inr2(p.grand_total || p.value)}</td>
              <td className="text-xs">{p.approver_required || '—'}</td>
              <td><span className={`tag ${statusColor(p.status)}`}>{p.status}</span></td>
              <td className="whitespace-nowrap" onClick={e => e.stopPropagation()}>
                <div className="flex items-center gap-1">
                  {p.status === 'Pending' && canApprovePO(user, p, matrix) && (
                    <button data-testid="approve-po-btn" onClick={e => approve(e, p.id)}
                      className="text-xs px-2 py-1 bg-[#3b4872] text-white rounded-sm hover:bg-[#2c3658] font-display font-semibold">Approve</button>
                  )}
                  <button onClick={e => downloadPdf(e, p)} title="Download PDF" disabled={!!pdfBusy}
                    className="p-1 text-slate-500 hover:text-[#3b4872] disabled:opacity-50" data-testid={`po-pdf-btn-${p.id}`}>
                    {pdfBusy === p.id ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                  </button>
                  <button data-testid={`po-notes-btn-${p.id}`} onClick={e => { e.stopPropagation(); setNotesPo(notesPo?.id === p.id ? null : p); }}
                    className="text-xs px-2 py-1 border border-slate-300 rounded-sm hover:border-[#3b4872] hover:text-[#3b4872] font-display font-semibold">
                    Notes
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {creating && <ManualPOModal onClose={() => setCreating(false)} onSaved={() => { setCreating(false); onReload(); }} activeProject={activeProject} />}
      {importing && <POImportWizard onClose={() => setImporting(false)} onImported={onReload} />}
      {notesPo && (
        <div className="mt-3 p-4 border border-slate-200 rounded-sm bg-slate-50">
          <div className="text-xs font-bold text-slate-700 mb-2">Notes — {notesPo.po_no}</div>
          <p className="text-xs text-slate-600 whitespace-pre-line">{notesPo.notes || 'No notes.'}</p>
          <button onClick={() => setNotesPo(null)} className="mt-2 text-xs text-slate-500 hover:text-[#3b4872] underline">Close</button>
        </div>
      )}
      {execReasonPrompt && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" data-testid="po-exec-reason-modal">
          <div className="bg-white rounded-sm p-5 w-full max-w-md">
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-amber-600">Exec Override</div>
            <p className="text-sm text-slate-700 mt-1 mb-3">You created this PO — approving it yourself needs a short written reason for the audit trail.</p>
            <textarea autoFocus rows={3} value={execReasonText} onChange={e => setExecReasonText(e.target.value)}
              placeholder="e.g. Urgent site requirement, no alternate approver available today"
              className="w-full text-sm border border-slate-300 rounded-sm px-3 py-2 focus:outline-none focus:border-[#3b4872]"
              data-testid="po-exec-reason-input" />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => { setExecReasonPrompt(null); setExecReasonText(''); }} disabled={execReasonBusy}
                className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-sm disabled:opacity-50">Cancel</button>
              <button onClick={submitExecReason} disabled={execReasonBusy}
                className="px-3 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm font-display font-bold disabled:opacity-50"
                data-testid="po-exec-reason-submit">
                {execReasonBusy ? 'Approving…' : 'Approve with reason'}
              </button>
            </div>
          </div>
        </div>
      )}
      {total > limit && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <Pagination page={page} pageSize={limit} total={total}
            start={(page-1)*limit} end={Math.min(page*limit,total)}
            onPageChange={setPage} onPageSizeChange={setLimit}
            pageSizeOptions={[20,50,100]} label="POs" data-testid="po-pagination" />
        </div>
      )}

      {/* PO Detail Drawer */}
      {detailPo && (
        <PODetailDrawer
          po={detailPo}
          loading={detailLoading}
          onClose={closeDetail}
          onChanged={() => { onReload(); openDetail(detailPo); }}
          user={user}
          matrix={matrix}
          onApprove={async () => {
            try {
              await apiPost(`/po/${detailPo.id}/approve`, {});
              toast.success('PO approved'); onReload(); setDetailPo(null);
            } catch (err) {
              const detail = err?.response?.data?.detail;
              if (detail?.code === 'EXEC_OVERRIDE_REASON_REQUIRED') {
                setExecReasonPrompt({ poId: detailPo.id, fromDrawer: true });
              } else {
                toast.error(detail?.message || detail || 'Approval failed');
              }
            }
          }}
          onReject={async (reason) => {
            try {
              const r = await apiPost(`/po/${detailPo.id}/reject`, { reason });
              toast.success(`PO rejected · now ${r.status}`); onReload(); setDetailPo(null);
            } catch (err) { const d = err?.response?.data?.detail; toast.error(typeof d === 'string' ? d : 'Reject failed'); }
          }}
          onDownloadPdf={e => downloadPdf(e, detailPo)}
          pdfBusy={pdfBusy === detailPo.id}
        />
      )}
    </div>
  );
};

// ─── PO Detail Drawer ────────────────────────────────────────────────────────
function PODetailDrawer({ po, loading, onClose, onApprove, onReject, onDownloadPdf, pdfBusy, user, matrix, onChanged }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const gstSplit = po?.gst_split_type || 'intra';
  const isIntra = gstSplit === 'intra';
  const [editMode, setEditMode] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  useEffect(() => {
    if (editMode || rejecting) return undefined;
    const onKey = e => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, editMode, rejecting]);
  const [rejectReason, setRejectReason] = useState('');
  const canEdit = ['Pending', 'Draft', 'Rejected'].includes(po?.status) && !po?.revision_pending;
  const canRevise = ['Approved', 'Partially Received'].includes(po?.status) || po?.revision_pending;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="po-detail-drawer">
      <button className="flex-1 bg-black/40 backdrop-blur-[2px]" onClick={onClose} />
      <div className="w-full max-w-4xl bg-white border-l border-slate-200 flex flex-col shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between shrink-0">
          <div>
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872]">Purchase Order Detail</div>
            <h2 className="font-display text-2xl font-extrabold tracking-tight mt-0.5 flex items-center gap-2">
              {po.po_no || '—'}
              {Number(po.revision) > 0 && <span className="text-xs tag bg-indigo-50 text-[#3b4872]" data-testid="po-drawer-revision">Rev {po.revision} · {fmtDMY(po.revised_at || po.updated_at)}</span>}
              {po.status && <span className={`text-sm tag ${statusColor(po.status)}`}>{po.status}</span>}
              {po.is_direct_po && <span className="text-xs tag bg-amber-100 text-amber-700">Direct PO</span>}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {po.status === 'Pending' && canApprovePO(user, po, matrix) && (
              <button onClick={onApprove} className="px-4 py-2 bg-emerald-600 text-white text-sm rounded-sm font-display font-semibold hover:bg-emerald-700" data-testid="po-drawer-approve-btn">
                Approve
              </button>
            )}
            {po.status === 'Pending' && canApprovePO(user, po, matrix) && (
              <button onClick={() => setRejecting(true)} className="px-4 py-2 border border-red-500 text-red-600 text-sm rounded-sm font-display font-semibold hover:bg-red-50" data-testid="po-drawer-reject-btn">
                Reject
              </button>
            )}
            <button onClick={onDownloadPdf} disabled={pdfBusy} className="flex items-center gap-1.5 px-3 py-2 border border-[#3b4872] text-[#3b4872] text-sm rounded-sm font-display font-semibold hover:bg-indigo-50 disabled:opacity-60" data-testid="po-drawer-pdf-btn">
              {pdfBusy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} {pdfBusy ? 'Generating…' : 'Download PDF'}
            </button>
            <button onClick={onClose} className="w-9 h-9 flex items-center justify-center text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-sm" data-testid="po-drawer-close-btn">
              <X size={18} />
            </button>
          </div>
        </div>

        {!loading && (
          <div className="px-6 pt-3 flex flex-wrap gap-2 items-center" data-testid="po-drawer-actions">
            {canEdit && <button onClick={() => setEditMode('edit')} className="px-3 py-1.5 text-xs border border-[#3b4872] text-[#3b4872] rounded-sm font-semibold" data-testid="po-drawer-edit-btn">Edit PO</button>}
            {canRevise && <button onClick={() => setEditMode('revise')} className="px-3 py-1.5 text-xs border border-[#3b4872] text-[#3b4872] rounded-sm font-semibold" data-testid="po-drawer-revise-btn">Revise PO</button>}
            <button onClick={() => setShowHistory(v => !v)} className={`px-3 py-1.5 text-xs rounded-sm font-semibold ${showHistory ? 'bg-[#3b4872] text-white' : 'border border-slate-300 text-slate-600'}`} data-testid="po-drawer-history-tab">History{(po.revision_history || []).length ? ` (${po.revision_history.length})` : ''}</button>
            {po.gst_split_type
              ? <span className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-sm px-2 py-1" data-testid="po-drawer-gst-hint">
                  Place of supply: <b>{po.place_of_supply || '—'}</b> · Vendor state: <b>{po.vendor_state || '—'}</b> → <b className="text-[#3b4872]">{po.gst_split_type === 'inter' ? 'IGST' : po.gst_split_type === 'mixed' ? 'Mixed' : 'CGST+SGST'}</b>
                </span>
              : <GstSplitHint vendorId={po.vendor_id} projectId={po.project_id} testid="po-drawer-gst-hint" />}
            {po.gst_split_type === 'mixed' && <span className="text-[11px] text-slate-500">Mixed: GST split decided per project line</span>}
          </div>
        )}
        {po.status === 'Rejected' && po.rejection_reason && (
          <div className="mx-6 mt-3 p-3 border border-red-200 bg-red-50 rounded-sm text-xs text-red-800" data-testid="po-rejection-banner">
            <b>Rejected</b> by {po.rejected_by_name || '—'} on {fmtDMY(po.rejected_at)} · Reason: {po.rejection_reason}
            <div className="text-red-700/80 mt-0.5">Edit the PO to re-submit it for approval.</div>
          </div>
        )}
        {rejecting && (
          <div className="mx-6 mt-3 p-3 border border-red-200 bg-red-50/50 rounded-sm space-y-2" data-testid="po-reject-panel">
            <div className="text-xs font-semibold text-red-700">{po.revision_pending ? 'Reject this revision — the PO goes back to its last approved version.' : 'Reject this PO — it goes back to the creator to edit and re-submit.'}</div>
            <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={2} placeholder="Reason for rejection (required)"
              className="w-full border border-slate-300 rounded-sm px-2 py-1 text-sm" data-testid="po-reject-reason" />
            <div className="flex justify-end gap-2">
              <button onClick={() => { setRejecting(false); setRejectReason(''); }} className="px-3 py-1 text-xs" data-testid="po-reject-cancel">Cancel</button>
              <button disabled={!rejectReason.trim()} onClick={() => onReject?.(rejectReason.trim())} className="px-3 py-1 text-xs bg-red-600 text-white rounded-sm disabled:opacity-50" data-testid="po-reject-confirm">Confirm reject</button>
            </div>
          </div>
        )}
        {editMode && <POEditModal po={po} mode={editMode} onClose={() => setEditMode(null)} onSaved={() => { setEditMode(null); onChanged?.(); }} />}
        {loading ? (
          <div className="flex-1 flex items-center justify-center"><Loader2 size={20} className="animate-spin text-[#3b4872]" /></div>
        ) : showHistory ? (
          <div className="flex-1 overflow-y-auto p-6"><POHistory po={po} /></div>
        ) : (
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Vendor + Ship-to */}
            <div className="grid grid-cols-2 gap-4">
              <div className="border border-slate-200 rounded p-4">
                <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-2">Supplier</div>
                <div className="font-semibold text-sm">{(po.vendor_block?.name || po.vendor || po.vendor_name) || '—'}</div>
                {po.vendor_block?.address && <div className="text-xs text-slate-500 mt-0.5">{po.vendor_block.address}, {po.vendor_block.city}, {po.vendor_block.state} {po.vendor_block.pincode}</div>}
                {po.vendor_block?.gstin && <div className="text-xs text-slate-600 mt-0.5">GSTIN: {po.vendor_block.gstin}</div>}
                {po.vendor_block?.pan && <div className="text-xs text-slate-600">PAN: {po.vendor_block.pan}</div>}
                {po.vendor_block?.msme_registered && <div className="text-xs text-emerald-700">MSME: {po.vendor_block.msme_number || 'Registered'}</div>}
              </div>
              <div className="border border-slate-200 rounded p-4">
                <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-2">Ship To / Project</div>
                <div className="font-semibold text-sm">{(po.ship_to_block?.project_name || po.project_block?.name || po.project || po.project_name) || '—'}{(po.ship_to_block?.project_code || po.project_block?.code) ? ` (${po.ship_to_block?.project_code || po.project_block?.code})` : ''}</div>
                {(po.ship_to_blocks || []).length > 1 && (po.ship_to_blocks.map((b, i) => (
                  <div key={b.project_id || i} className="text-xs text-slate-600 mt-1" data-testid={`po-detail-ship-to-${i}`}><b>{b.project_code || b.project_name}</b>: {b.site_address || '—'}{b.site_state ? `, ${b.site_state}` : ''}{b.site_contact_name ? ` · ${b.site_contact_name} ${b.site_contact_mobile || ''}` : ''}</div>)))}
                {(po.ship_to_blocks || []).length <= 1 && (po.ship_to_block?.site_address || po.ship_to) && <div className="text-xs text-slate-600 mt-1 whitespace-pre-line" data-testid="po-detail-ship-to"><span className="font-semibold text-slate-700">Ship To: </span>{po.ship_to_block?.site_address || po.ship_to}{[po.ship_to_block?.site_city, po.ship_to_block?.site_state].filter(Boolean).length ? `, ${[po.ship_to_block?.site_city, po.ship_to_block?.site_state].filter(Boolean).join(', ')}` : ''}{po.ship_to_block?.site_pincode ? ` - ${po.ship_to_block.site_pincode}` : ''}</div>}
                {(po.ship_to_block?.site_contact_name || po.ship_to_block?.site_contact_mobile) && <div className="text-xs text-slate-600 mt-0.5" data-testid="po-detail-site-contact">Site contact: {po.ship_to_block.site_contact_name || '—'} · M: {po.ship_to_block.site_contact_mobile || '—'}</div>}
                {po.delivery_state && <div className="text-xs text-slate-600 mt-0.5">Place of Supply: {po.delivery_state}</div>}
                {po.gst_split_type && <div className="text-xs text-slate-500">{po.gst_split_type === 'intra' ? 'Intra-state (CGST+SGST)' : 'Inter-state (IGST)'}</div>}
              </div>
            </div>

            {/* Line Items */}
            <div>
              <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-2">Line Items</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead className="bg-slate-800 text-white">
                    <tr>
                      <th className="text-left px-2 py-2">#</th>
                      <th className="text-left px-2 py-2">Item Code</th>
                      <th className="text-left px-2 py-2">Description</th>
                      <th className="text-left px-2 py-2">HSN</th>
                      <th className="text-right px-2 py-2">Qty</th>
                      <th className="text-left px-2 py-2">Unit</th>
                      <th className="text-right px-2 py-2">Rate</th>
                      <th className="text-right px-2 py-2">Taxable</th>
                      <th className="text-right px-2 py-2">GST%</th>
                      {isIntra ? <>
                        <th className="text-right px-2 py-2">CGST</th>
                        <th className="text-right px-2 py-2">SGST</th>
                      </> : <th className="text-right px-2 py-2">IGST</th>}
                      <th className="text-right px-2 py-2">Total</th>
                      <th className="text-right px-2 py-2">Rcvd</th>
                      <th className="text-right px-2 py-2">Bal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(po.items || []).map((it, i) => (
                      <tr key={i} className={`border-b border-slate-100 ${i % 2 ? 'bg-slate-50/40' : ''}`}>
                        <td className="px-2 py-1.5 text-slate-500">{i + 1}</td>
                        <td className="px-2 py-1.5 font-mono text-slate-600">{it.material_code || it.item_code || '—'}</td>
                        <td className="px-2 py-1.5 font-medium">
                          {it.description || it.material_name || it.name}
                          {it.specification && <div className="text-[10px] text-slate-500 font-normal">{it.specification}</div>}
                        </td>
                        <td className="px-2 py-1.5 font-mono text-slate-500">{it.hsn_code || '—'}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{it.qty}</td>
                        <td className="px-2 py-1.5">{it.uom || it.unit}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{it.rate != null ? inr2(it.rate) : '—'}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{it.taxable_value != null ? inr2(it.taxable_value) : '—'}</td>
                        <td className="px-2 py-1.5 text-right">{it.gst_rate != null ? `${it.gst_rate}%` : '—'}</td>
                        {isIntra ? <>
                          <td className="px-2 py-1.5 text-right tabular-nums">{it.cgst_amount != null ? inr2(it.cgst_amount) : '—'}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{it.sgst_amount != null ? inr2(it.sgst_amount) : '—'}</td>
                        </> : <td className="px-2 py-1.5 text-right tabular-nums">{it.igst_amount != null ? inr2(it.igst_amount) : '—'}</td>}
                        <td className="px-2 py-1.5 text-right tabular-nums font-bold">{it.line_total != null ? inr2(it.line_total) : inr2(it.qty * it.rate || 0)}</td>
                        <td className="px-2 py-1.5 text-right text-emerald-700">{it.received_qty != null ? it.received_qty : '—'}</td>
                        <td className={`px-2 py-1.5 text-right ${it.balance_qty > 0 ? 'text-amber-700' : 'text-slate-400'}`}>{it.balance_qty != null ? it.balance_qty : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-72 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-slate-600">Subtotal (Taxable)</span><span className="tabular-nums font-semibold">{inr2(po.subtotal_taxable || 0)}</span></div>
                {chargesFromPo(po).filter(c => Number(c.amount)).map((c, i) => (
                  <div key={c.id || i} className="flex justify-between" data-testid={`po-drawer-charge-${i}`}>
                    <span className="text-slate-600">{c.type}{c.description ? ` — ${c.description}` : ''} ({Number(c.gst_rate || 0)} %)</span>
                    <span className="tabular-nums">{inr2(c.amount)}</span></div>))}
                {chargesFromPo(po).some(c => Number(c.amount)) && <div className="flex justify-between" data-testid="po-drawer-taxable-total"><span className="text-slate-600">Taxable total</span><span className="tabular-nums">{inr2(Number(po.subtotal_taxable || 0) + chargesFromPo(po).reduce((t, c) => t + Number(c.amount || 0), 0))}</span></div>}
                {(isIntra || po.total_cgst > 0) && <>
                  <div className="flex justify-between"><span className="text-slate-600">CGST</span><span className="tabular-nums">{inr2(po.total_cgst || 0)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-600">SGST</span><span className="tabular-nums">{inr2(po.total_sgst || 0)}</span></div>
                </>}
                {(!isIntra || po.total_igst > 0) && <div className="flex justify-between"><span className="text-slate-600">IGST</span><span className="tabular-nums">{inr2(po.total_igst || 0)}</span></div>}
                {po.round_off !== 0 && po.round_off != null && <div className="flex justify-between text-xs"><span className="text-slate-500">Round Off</span><span className="tabular-nums">{inr2(po.round_off)}</span></div>}
                <div className="flex justify-between border-t border-slate-300 pt-1 font-bold text-base">
                  <span>GRAND TOTAL</span>
                  <span className="text-[#3b4872] tabular-nums">{inr2(po.grand_total || po.total_value || po.value || 0)}</span>
                </div>
                {po.amount_in_words && <div className="text-xs text-slate-500 italic">{po.amount_in_words}</div>}
              </div>
            </div>

            {/* Terms + Approval trail */}
            <div className="grid grid-cols-2 gap-4">
              <div className="border border-slate-200 rounded p-4 space-y-2">
                <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">Terms</div>
                {[['Payment', po.payment_terms || po.terms], ['Delivery', po.delivery_schedule], ['Price Basis', po.price_basis], ['Warranty', po.warranty_terms], ['LD Clause', po.ld_clause]].filter(([,v]) => v).map(([k,v]) => (
                  <div key={k} className="text-xs"><span className="font-semibold text-slate-700">{k}: </span><span className="text-slate-600">{v}</span></div>
                ))}
                {/* Item 8 (Fixes_Combined): the PO's actual T&C clauses */}
                {(po.tc_clauses || []).length > 0 && (
                  <div className="pt-2 border-t border-slate-100" data-testid="po-tc-clauses">
                    <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1">T&amp;C Clauses</div>
                    <ol className="list-decimal pl-4 space-y-0.5">
                      {po.tc_clauses.map((c, i) => <li key={i} className="text-[11px] text-slate-600">{c}</li>)}
                    </ol>
                  </div>
                )}
                {po.extra_clause && <div className="text-[11px] text-slate-600"><span className="font-semibold">Extra clause: </span>{po.extra_clause}</div>}
              </div>
              <div className="border border-slate-200 rounded p-4 space-y-2">
                <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">Approval Trail</div>
                <div className="text-xs space-y-1">
                  <div><span className="text-slate-500">Created by:</span> <span className="font-medium">{po.created_by_name || po.created_by || '—'}</span> <span className="text-slate-400">{po.created_at ? formatDate(po.created_at) : ''}</span></div>
                  {po.approved_by && <div><span className="text-slate-500">Approved by:</span> <span className="font-medium text-emerald-700">{po.approved_by}</span> <span className="text-slate-400">{po.approved_at ? formatDate(po.approved_at) : ''}</span></div>}
                  {po.self_approved && po.self_approved_basis === 'procurement_group' && <div data-testid="po-trail-self-approved"><span className="tag bg-amber-100 text-amber-700">Self-approved (Procurement team)</span></div>}
                  {po.approver_tier && <div><span className="text-slate-500">Tier:</span> <span className="font-medium">{po.approver_tier}</span></div>}
                  {po.amendments?.length > 0 && <div><span className="text-slate-500">Amendments:</span> {po.amendments.length}</div>}
                </div>
                {/* Linked docs */}
                <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mt-2">Linked Documents</div>
                <div className="text-xs space-y-0.5">
                  {po.rfq_no && <div><span className="text-slate-500">RFQ:</span> <span className="font-mono text-[#3b4872]">{po.rfq_no}</span></div>}
                  {(po.mpr_refs?.length ? po.mpr_refs : (po.mpr_no ? [{ mpr_no: po.mpr_no, mpr_date: po.mpr_date }] : [])).map((r, i) => (
                    <div key={r.mpr_no || i} data-testid={i === 0 ? 'po-pr-no' : `po-pr-no-${i}`}><span className="text-slate-500">MPR No:</span> <span className="font-mono text-[#3b4872]">{r.mpr_no}</span>{r.mpr_date && <span className="text-slate-400"> · {fmtDMY(r.mpr_date)}</span>}</div>
                  ))}
                  {po.grn_nos?.length > 0 && <div><span className="text-slate-500">GRNs:</span> {po.grn_nos.join(', ')}</div>}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ManualPOModal({ onClose, onSaved, activeProject, preset }) {
  const ff = useFormFieldHelpers('po_create');
  const [projects, setProjects] = useState([]);
  const [approvedMprs, setApprovedMprs] = useState([]);
  const [tcSets, setTcSets] = useState([]);
  const [form, setForm] = useState({
    vendor_id: '', vendor: '', project_id: activeProject || '', project: '',
    po_date: new Date().toISOString().slice(0, 10),
    delivery_date: '', delivery_address: '',
    items: [{ material_id: '', description: '', qty: 1, unit: 'Nos', rate: 0, hsn_code: '', gst_rate: 18 }],
    terms: '',
    bypass_reason: '',
    bypass_approved_by: '',
    mpr_id: '',
    tc_set_id: '',
    extra_clause: '',
    charges: [],
  });
  const [saving, setSaving] = useState(false);
  const [gstSplit, setGstSplit] = useState('intra');
  // A2 — same 5-year bound the backend now enforces, surfaced as HTML5 min/max hints.
  const today = new Date();
  const minDateStr = new Date(today.getFullYear() - 5, today.getMonth(), today.getDate()).toISOString().slice(0, 10);
  const maxDateStr = new Date(today.getFullYear() + 5, today.getMonth(), today.getDate()).toISOString().slice(0, 10);

  useEffect(() => {
    apiGet('/projects').then(setProjects).catch((e) => toast.error(e?.response?.status === 403
      ? 'You do not have access to the project list — ask an admin to grant Projects view access to raise a PO.'
      : 'Could not load projects'));
    apiGet('/settings/tc-library').then((d) => setTcSets(d?.sets || [])).catch(() => {});
  }, []);
  const selProject = projects.find(p => p.id === form.project_id);
  // Q2 (Fixes round 6): pre-fill delivery_address from the project, same as
  // the Wizard's prefill (R7, round 4) — projects only carry address /
  // site_address / location, never delivery_address itself.
  // V1 (Fixes round 9): refill (not just fill-if-empty) whenever the project
  // actually CHANGES — switching project A → B must not leave A's site behind.
  const prevProjectIdRef = useRef(form.project_id);
  useEffect(() => {
    if (form.project_id && form.project_id !== prevProjectIdRef.current) {
      const addr = selProject?.site_address || selProject?.address || selProject?.location || '';
      if (addr) setForm(f => ({ ...f, delivery_address: addr }));
    }
    prevProjectIdRef.current = form.project_id;
  }, [form.project_id, selProject]);
  // Item 5 (Fixes_Combined): only the selected project's Approved MPRs.
  useEffect(() => {
    if (!form.project_id) { setApprovedMprs([]); return; }
    apiGet('/mpr', { status: 'PM Approved', project_id: form.project_id, limit: 100 })
      .then((d) => setApprovedMprs((d?.items || d || []).filter(m => m.project_id === form.project_id)))
      .catch(() => setApprovedMprs([]));
  }, [form.project_id]);
  // #7/#8: ticked MPR lines (any project) are added with their MPR / project links.
  const addMprLines = async (lines) => {
    const mats = await Promise.all(lines.map(li => apiGet(`/material-master/${li.material_id}`).catch(() => null)));
    const rows = lines.map((li, i) => ({
      material_id: li.material_id, description: li.material_name || '', material_code: mats[i]?.material_id || '',
      qty: li.qty, unit: li.unit || mats[i]?.uom || 'Nos', rate: 0, hsn_code: mats[i]?.hsn_code || '',
      gst_rate: mats[i]?.gst_rate != null ? mats[i].gst_rate : 18,
      mpr_id: li.mpr_id, mpr_line_id: li.mpr_line_id, mpr_no: li.mpr_no, project_id: li.project_id, project_name: li.project_name, balance_qty: li.balance_qty,
    }));
    setForm(f => {
      const keep = f.items.filter(it => it.material_id);
      // Dedupe by MPR line — StrictMode double-runs the preset effect, and a line can't be ordered twice on one PO.
      const have = new Set(keep.map(it => it.mpr_line_id).filter(Boolean));
      const fresh = rows.filter(r => !r.mpr_line_id || !have.has(r.mpr_line_id));
      const first = rows[0];
      return { ...f, items: [...keep, ...fresh], mpr_id: f.mpr_id || first?.mpr_id || '', project_id: f.project_id || first?.project_id || '' };
    });
  };
  const lineProjects = [...new Set(form.items.map(it => it.project_id).filter(Boolean))]
    .map(id => projects.find(p => p.id === id) || { id, name: form.items.find(it => it.project_id === id)?.project_name });
  useEffect(() => {
    if (!preset?.lines?.length) return;
    addMprLines(preset.lines.map(l => ({ ...l, qty: l.balance_qty, mpr_line_id: l.line_id, unit: l.uom })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset]);
  // Procurement_7Sep2026 §4: linking a routine (below-threshold) Approved MPR
  // means no bypass justification is required — the backend re-checks this
  // authoritatively; this just relaxes the client-side requirement so the
  // "routine purchase" path isn't forced through the bypass fields.
  const bypassRequired = !form.mpr_id && !form.items.some(it => it.mpr_id);

  const lineTotal = (it) => (Number(it.qty || 0) * Number(it.rate || 0));
  const total = form.items.reduce((s, it) => s + lineTotal(it), 0);

  const updateItem = (idx, key, val) => {
    setForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, [key]: val } : it) }));
  };
  const selectItemMaterial = (idx, m) => {
    setForm(f => ({ ...f, items: f.items.map((it, i) => i === idx
      ? (m ? { ...it, material_id: m.id, description: m.name, material_code: m.material_id, unit: it.unit || m.uom || 'Nos',
                hsn_code: m.hsn_code || '', gst_rate: m.gst_rate != null ? m.gst_rate : 18 }
           : { ...it, material_id: '', description: '', hsn_code: '', gst_rate: 18 })
      : it) }));
  };
  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { material_id: '', description: '', qty: 1, unit: 'Nos', rate: 0, hsn_code: '', gst_rate: 18 }] }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vendor_id) { toast.error('Select an empanelled vendor'); return; }
    if (!form.items.some(it => it.material_id && it.qty > 0)) {
      toast.error('Add at least one line item with a Material Master item'); return;
    }
    // A1 — a Manual PO always bypasses the MPR→RFQ chain, so both fields are
    // always required here (the backend already enforces this; this just
    // catches it before a round-trip). §4: not required when a routine
    // (below-threshold) Approved MPR is linked instead.
    if (bypassRequired && (!form.bypass_reason.trim() || !form.bypass_approved_by.trim())) {
      toast.error('Bypass Reason and Approved By are required for a manual PO with no linked MPR/RFQ.'); return;
    }
    // A2 — PO Date / Expected Delivery sanity, mirrors the backend check.
    if (form.delivery_date && form.po_date && form.delivery_date < form.po_date) {
      toast.error('Expected Delivery cannot be before the PO Date.'); return;
    }
    if (form.po_date && (form.po_date < minDateStr || form.po_date > maxDateStr)) {
      toast.error('PO Date must be within 5 years of today.'); return;
    }
    if (form.delivery_date && (form.delivery_date < minDateStr || form.delivery_date > maxDateStr)) {
      toast.error('Expected Delivery must be within 5 years of today.'); return;
    }
    setSaving(true);
    try {
      const proj = projects.find(p => p.id === form.project_id);
      const payload = {
        vendor_id: form.vendor_id, vendor: form.vendor,
        project_id: form.project_id, project: proj?.name || proj?.code || '',
        po_date: form.po_date, delivery_date: form.delivery_date,
        delivery_address: form.delivery_address,
        items: form.items, terms: form.terms,
        value: Math.round(total),
        status: 'Pending', source: 'Manual',
        bypass_reason: form.bypass_reason || undefined,
        bypass_approved_by: form.bypass_approved_by || undefined,
        mpr_id: form.mpr_id || undefined,
        charges: cleanCharges(form.charges),
        tc_set_id: form.tc_set_id || undefined,
        extra_clause: form.extra_clause || undefined,
      };
      const created = await apiPost('/po', payload);
      toast.success(`PO created · ${inr2(created?.grand_total ?? total)}`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create PO');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-white rounded-sm w-full max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="manual-po-modal">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872]">New</div>
            <h3 className="font-display font-bold text-xl mt-1">Manual Purchase Order</h3>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {/* #10: vendor is always shown and always required, whatever the form config says. */}
            <div>
              <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">
                Vendor<span className="text-red-500 ml-0.5">*</span>
              </label>
              <VendorPicker
                value={form.vendor_id}
                displayName={form.vendor}
                onSelect={(v) => setForm({ ...form, vendor_id: v?.id || '', vendor: v?.name || '' })}
                placeholder="Search vendor by name, code or GSTIN…"
                testid="po-vendor-picker"
              />
            </div>
            {ff.showField('project_id') && (
              <div>
                <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">
                  {ff.labelOf('project_id', 'Project')}{ff.reqStar('project_id', true)}
                </label>
                <select value={form.project_id} onChange={e => setForm({ ...form, project_id: e.target.value })} required={ff.isReq('project_id', true)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white" data-testid="po-project-select">
                  <option value="">— Select project —</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.name}` : p.name}</option>)}
                </select>
                {selProject && !selProject.site_state && (
                  <div className="text-[11px] text-amber-700 mt-1" data-testid="po-site-state-warning">
                    Project site state is missing — GST split may be wrong. Update the project.
                  </div>
                )}
              </div>
            )}
            <div className="col-span-2">
              <MprLinePicker projects={projects} onAdd={addMprLines} testid="po-mpr-picker" />
            </div>
            <div className="col-span-2">
              <GstSplitHint vendorId={form.vendor_id} projectId={lineProjects[0]?.id || form.project_id} onSplit={setGstSplit} testid="po-gst-split-hint" />
            </div>
            {ff.showField('po_date') && (
              <div>
                <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">
                  {ff.labelOf('po_date', 'PO Date')}{ff.reqStar('po_date', true)}
                </label>
                <input type="date" value={form.po_date} onChange={e => setForm({ ...form, po_date: e.target.value })} required={ff.isReq('po_date', true)}
                  min={minDateStr} max={maxDateStr}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              </div>
            )}
            {ff.showField('delivery_date') && (
              <div>
                <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">
                  {ff.labelOf('delivery_date', 'Expected Delivery')}{ff.reqStar('delivery_date', true)}
                </label>
                <input type="date" value={form.delivery_date} onChange={e => setForm({ ...form, delivery_date: e.target.value })} required={ff.isReq('delivery_date', true)}
                  min={form.po_date || minDateStr} max={maxDateStr}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              </div>
            )}
            {ff.showField('delivery_address') && (
              <div className="col-span-2">
                <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">
                  {ff.labelOf('delivery_address', 'Delivery Address')}{ff.reqStar('delivery_address', false)}
                </label>
                <textarea value={form.delivery_address} onChange={e => setForm({ ...form, delivery_address: e.target.value })} required={ff.isReq('delivery_address', false)} rows={2}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              </div>
            )}
          </div>

          <div className="border border-slate-200 rounded-sm overflow-visible">
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <span className="kpi-overline">Line Items{ff.reqStar('items', true)}</span>
              <button type="button" onClick={addItem} data-testid="po-add-item-btn" className="text-xs text-[#3b4872] font-display font-bold uppercase tracking-wider hover:underline">+ Add row</button>
            </div>
            <table className="w-full text-xs">
              <thead className="bg-slate-50/50 text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-3 py-1.5">Material (from Master)</th>
                  <th className="text-left px-3 py-1.5 w-20">HSN</th>
                  <th className="text-right px-3 py-1.5 w-16">Qty</th>
                  <th className="text-left px-3 py-1.5 w-16">Unit</th>
                  <th className="text-right px-3 py-1.5 w-24">Rate (₹)</th>
                  <th className="text-right px-3 py-1.5 w-16">GST%</th>
                  <th className="text-right px-3 py-1.5 w-24">Total</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {form.items.map((it, idx) => (
                  <tr key={idx} className="border-t border-slate-100" data-testid={`po-item-row-${idx}`}>
                    <td className="px-2 py-1 min-w-[180px]"><MaterialPicker compact value={it.material_id} displayName={it.description} onSelect={(m) => selectItemMaterial(idx, m)} placeholder="Pick material…" testid={`po-item-material-${idx}`} />
                      {it.mpr_no && <div className="text-[10px] text-slate-500 mt-0.5" data-testid={`po-item-mpr-${idx}`}>{[`MPR ${it.mpr_no}`, it.project_name, it.balance_qty != null ? `balance ${it.balance_qty}` : ''].filter(Boolean).join(' · ')}</div>}</td>
                    <td className="px-2 py-1"><input value={it.hsn_code || ''} onChange={e => updateItem(idx, 'hsn_code', e.target.value)} placeholder="HSN" className="w-full px-2 py-1 border border-slate-300 rounded-sm text-xs font-mono" data-testid={`po-item-hsn-${idx}`} /></td>
                    <td className="px-2 py-1"><input type="number" min="0" step="any" max={it.balance_qty ?? undefined} value={it.qty} onChange={e => updateItem(idx, 'qty', it.balance_qty != null ? Math.min(Number(e.target.value), it.balance_qty) : e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded-sm text-xs text-right tabular-nums" data-testid={`po-item-qty-${idx}`} /></td>
                    <td className="px-2 py-1"><input value={it.unit} onChange={e => updateItem(idx, 'unit', e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded-sm text-xs" /></td>
                    <td className="px-2 py-1"><input type="number" min="0" step="any" value={it.rate} onChange={e => updateItem(idx, 'rate', e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded-sm text-xs text-right tabular-nums" /></td>
                    <td className="px-2 py-1"><input type="number" min="0" max="28" step="0.01" value={it.gst_rate ?? 18} onChange={e => updateItem(idx, 'gst_rate', parseFloat(e.target.value))} className="w-full px-2 py-1 border border-slate-300 rounded-sm text-xs text-right tabular-nums" data-testid={`po-item-gst-${idx}`} /></td>
                    <td className="text-right px-3 py-1 tabular-nums font-display font-semibold">{inr2(lineTotal(it))}</td>
                    <td className="text-center"><button type="button" onClick={() => removeItem(idx)} disabled={form.items.length === 1} className="text-rose-500 hover:text-rose-700 disabled:opacity-30"><Trash2 size={12} /></button></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 border-t border-slate-200">
                  <td colSpan="6" className="text-right px-3 py-2 text-[10px] font-display font-bold uppercase tracking-widest">Subtotal (excl. GST)</td>
                  <td className="text-right px-3 py-2 font-display font-bold text-base tabular-nums" data-testid="po-total">{inr2(total)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {ff.showField('terms') && (
            <div>
              <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">
                {ff.labelOf('terms', 'Terms & Conditions')}{ff.reqStar('terms', false)}
              </label>
              <textarea value={form.terms} onChange={e => setForm({ ...form, terms: e.target.value })} required={ff.isReq('terms', false)} rows={3}
                placeholder="Payment terms, delivery conditions, warranty…"
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
            </div>
          )}

          {/* Procurement_7Sep2026 §7: pick a standard T&C set + an optional extra clause */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Standard T&C Set</label>
              <select value={form.tc_set_id} onChange={e => setForm({ ...form, tc_set_id: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white" data-testid="po-tc-set-select">
                <option value="">— Default set —</option>
                {tcSets.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Extra Clause (optional)</label>
              <input value={form.extra_clause} onChange={e => setForm({ ...form, extra_clause: e.target.value })}
                placeholder="One additional clause for this PO only" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="po-extra-clause-input" />
            </div>
          </div>

          <ChargesTable charges={form.charges} onChange={(c) => setForm(f => ({ ...f, charges: c }))} items={form.items}
            projects={lineProjects} intra={gstSplit === 'intra'} testid="po-charges" />
          <POTotalsPreview items={form.items} charges={form.charges} />

          {/* Bug 4 fix: Chain bypass documentation — required only when no MPR/RFQ linked */}
          {bypassRequired && (
          <div className="border border-amber-200 bg-amber-50 rounded-sm p-4 space-y-3">
            <div className="text-[10px] font-display font-bold uppercase tracking-wider text-amber-700">
              Procurement Chain Bypass (required — no MPR/RFQ linked)
            </div>
            <p className="text-xs text-amber-800">
              Manual POs without a linked routine-Approved MPR or awarded RFQ bypass the MPR → RFQ → CS approval chain.
              Both fields below are required in that case.
            </p>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 block mb-1">Bypass Reason *</label>
              <input value={form.bypass_reason} onChange={e => setForm({ ...form, bypass_reason: e.target.value })}
                placeholder="e.g. Emergency procurement, vendor sole-source, rate-contract PO"
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" required
                data-testid="po-bypass-reason" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500 block mb-1">Approved By (Senior Name) *</label>
              <input value={form.bypass_approved_by} onChange={e => setForm({ ...form, bypass_approved_by: e.target.value })}
                placeholder="Name of MD/CFO/SCM Head who approved the bypass"
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" required
                data-testid="po-bypass-approver" />
            </div>
          </div>
          )}
        </div>
        <div className="sticky bottom-0 bg-slate-50 border-t border-slate-200 px-5 py-3 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-white font-display">Cancel</button>
          <button type="submit" disabled={saving} data-testid="manual-po-save-btn" className="px-4 py-2 text-sm bg-[#3b4872] hover:bg-[#2c3658] text-white rounded-sm font-display font-semibold disabled:opacity-50">
            {saving ? 'Creating…' : 'Create PO'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ----- Quote History Modal -----
function QuoteHistoryModal({ rfqId, rfqNo, vendorFilter, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGet(`/rfq/${rfqId}/quote-history`)
      .then(setData)
      .catch(() => toast.error('Failed to load quote history'))
      .finally(() => setLoading(false));
  }, [rfqId]);

  const histories = data?.vendor_histories || [];
  const filtered = vendorFilter
    ? histories.filter(vh => vh.vendor_id === vendorFilter || vh.vendor_name === vendorFilter)
    : histories;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-sm shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872]">Quote Rate History</div>
            <h3 className="font-display font-bold text-base mt-0.5">{rfqNo}{vendorFilter ? ` · ${filtered[0]?.vendor_name || vendorFilter}` : ' · All Vendors'}</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-5">
          {loading && <div className="text-center py-8 text-slate-400 text-sm">Loading…</div>}
          {!loading && filtered.length === 0 && <div className="text-center py-8 text-slate-400 text-sm">No quote history found.</div>}
          {filtered.map(vh => (
            <div key={vh.vendor_id} className="space-y-2" data-testid={`qh-vendor-${vh.vendor_id}`}>
              <div className="font-display font-bold text-sm text-[#3b4872]">{vh.vendor_name}</div>
              {/* Current live quote */}
              {vh.current && (
                <div className="border border-emerald-200 bg-emerald-50/40 rounded-sm p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-display font-bold uppercase tracking-wider text-emerald-700">Current Quote</span>
                    <span className="text-[11px] text-slate-500">{formatDate(vh.current.submitted_at)}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div><span className="text-slate-500">Rate: </span><span className="font-bold">{inr2(vh.current.rate || vh.current.total_value)}</span></div>
                    <div><span className="text-slate-500">Delivery: </span><span className="font-semibold">{vh.current.delivery_days ? `${vh.current.delivery_days}d` : '—'}</span></div>
                    <div><span className="text-slate-500">Terms: </span><span className="font-semibold">{vh.current.payment_terms || '—'}</span></div>
                  </div>
                  <div className="text-[10px] text-slate-400 mt-1">
                    Source: {vh.current.source === 'vendor_portal' ? 'Vendor Portal' : 'Internal Staff'}
                  </div>
                  {/* R4 (Fixes round 4): show each line's own rate per version, not just the total */}
                  {Array.isArray(vh.current.line_items) && vh.current.line_items.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-emerald-200/60 space-y-0.5" data-testid="qh-current-lines">
                      {vh.current.line_items.map((li, li_i) => (
                        <div key={li_i} className="flex items-center justify-between text-[11px]">
                          <span className="text-slate-600 truncate pr-2">{li.material_name || li.description || `Line ${li_i + 1}`}</span>
                          <span className="font-semibold shrink-0">{inr2(li.rate)} {li.uom || li.unit || ''}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {/* History entries */}
              {vh.history.map((h, i) => (
                <div key={i} className="border border-slate-200 rounded-sm p-3 bg-slate-50/30" data-testid={`qh-entry-${i}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">Superseded Version</span>
                    <div className="text-right">
                      <div className="text-[11px] text-slate-500">Submitted {formatDate(h.submitted_at)}</div>
                      <div className="text-[10px] text-slate-400">Replaced {formatDate(h.superseded_at)}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div><span className="text-slate-500">Rate: </span><span className="font-semibold">{inr2(h.rate || h.total_value)}</span></div>
                    <div><span className="text-slate-500">Delivery: </span><span className="font-semibold">{h.delivery_days ? `${h.delivery_days}d` : '—'}</span></div>
                    <div><span className="text-slate-500">Terms: </span><span className="font-semibold">{h.payment_terms || '—'}</span></div>
                  </div>
                  <div className="text-[10px] text-slate-400 mt-1">
                    Source: {h.source === 'vendor_portal' ? 'Vendor Portal' : 'Internal Staff'} ·
                    Replaced by: {h.superseded_by || '—'} ({h.superseded_by_source || '—'})
                  </div>
                  {Array.isArray(h.line_items) && h.line_items.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-slate-200 space-y-0.5" data-testid={`qh-history-lines-${i}`}>
                      {h.line_items.map((li, li_i) => (
                        <div key={li_i} className="flex items-center justify-between text-[11px]">
                          <span className="text-slate-500 truncate pr-2">{li.material_name || li.description || `Line ${li_i + 1}`}</span>
                          <span className="font-semibold shrink-0">{inr2(li.rate)} {li.uom || li.unit || ''}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {vh.history.length === 0 && !vh.current && (
                <div className="text-xs text-slate-400 italic">No submissions recorded.</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ----- Approval Matrix -----
const ApprovalMatrix = ({ matrix }) => (
  <div>
    <div className="kpi-overline mb-3">Approval Authority Matrix</div>
    <p className="text-sm text-slate-600 mb-5 max-w-2xl">PO and CS approval is auto-routed based on the value tier below. Configure tiers in Settings → Approval Workflows.</p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {matrix.map((t, i) => {
        const prev = i === 0 ? 0 : matrix[i-1].max;
        return (
          <div key={i} className="bg-white border border-slate-200 rounded-sm p-5 hover:border-[#3b4872]/40">
            <div className="kpi-overline mb-1">Tier {i+1}</div>
            <div className="font-display text-xl font-extrabold tracking-tight">{t.approver}</div>
            <div className="text-xs text-slate-600 mt-2">
              {t.max ? `${inrCompact(prev)} – ${inrCompact(t.max)}` : `Above ${inrCompact(prev)}`}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);